/**
 * Colourway Composer — Phase 1 landing.
 *
 * Layer 1 (Pantone) is read-only browse for now (mgmt handled at
 * Tech Ops → Pantone module). Layer 2 (Colourant SKU) gets full CRUD.
 * Layer 3 (Vendor MB attach/preferred) ships in Phase 2.
 *
 * The page lives at /colourway (mounted under Demand Hub per the
 * "design team is part of Demand" decision).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import React from "react";
import axios from "axios";
import { toast } from "sonner";
import useAuthStore from "@/store/authStore";
import { useHasAnyRole } from "@/utils/roleGuard";
import ColourwayMock from "./ColourwayMock";   // 3-tile Bidso SKU Composer landing (Feb 2026)
import {
  Palette,
  Plus,
  Search,
  AlertCircle,
  Layers,
  Beaker,
  Eye,
  Edit2,
  Trash2,
  Download,
  Upload,
  Loader2,
  ChevronDown,
  ChevronRight,
  Star,
  Link2,
  Link2Off,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const APPLICATIONS = [
  { value: "INJECTION", label: "Injection (Plastic / INP)" },
  { value: "POWDER_COAT", label: "Powder Coat (Metal / INMFAB)" },
  { value: "PAINT", label: "Paint" },
  { value: "DECAL", label: "Decal / Print" },
  { value: "OTHER", label: "Other" },
];

function StatusBadge({ status }) {
  const variants = {
    ACTIVE: "bg-emerald-100 text-emerald-800 border-emerald-200",
    DRAFT: "bg-amber-100 text-amber-800 border-amber-200",
    DEPRECATED: "bg-stone-200 text-stone-700 border-stone-300",
  };
  return (
    <Badge variant="outline" className={variants[status] || ""} data-testid={`status-badge-${status}`}>
      {status}
    </Badge>
  );
}

function ColourSwatch({ hex }) {
  if (!hex) return <span className="inline-block w-5 h-5 rounded-full border border-stone-300 bg-stone-100" />;
  return (
    <span
      className="inline-block w-5 h-5 rounded-full border border-stone-300"
      style={{ background: hex }}
      title={hex}
    />
  );
}

export default function Colourway() {
  const [tab, setTab] = useState("composer");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/colourway/stats`);
        setStats(data);
      } catch (e) {
        // non-fatal
      }
    };
    load();
  }, [tab]);

  return (
    <div className="space-y-6" data-testid="colourway-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Palette className="w-7 h-7 text-rose-600" />
            Colourway Composer
          </h1>
          <p className="text-stone-600 text-sm mt-1 max-w-2xl">
            The shade language for the design team. Pick a Pantone, the
            system maps it to a Colourant SKU, procurement attaches one or
            more vendor master batches underneath.
          </p>
        </div>
      </div>

      {/* Quick stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="colourway-stats">
          <StatCard label="Pantone Shades" value={stats.pantone_shades_active} icon={Palette} />
          <StatCard label="Colourant SKUs" value={stats.colourant_skus_active} icon={Layers} />
          <StatCard
            label="Colourants w/o Vendor MB"
            value={stats.colourant_skus_without_vendor_mb}
            icon={AlertCircle}
            warning={stats.colourant_skus_without_vendor_mb > 0}
            subtitle={
              stats.colourant_skus_without_vendor_mb_incl_orphans != null
                ? `${stats.colourant_skus_without_vendor_mb_incl_orphans} incl. orphans`
                : null
            }
          />
          <StatCard label="Vendor MBs" value={stats.vendor_mbs_total} icon={Beaker} />
          <StatCard
            label="MBs Unlinked"
            value={stats.vendor_mbs_unlinked_to_colourant}
            icon={AlertCircle}
            warning={stats.vendor_mbs_unlinked_to_colourant > 0}
          />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList data-testid="colourway-tabs">
          <TabsTrigger value="composer" data-testid="tab-composer">Bidso SKU Composer</TabsTrigger>
          <TabsTrigger value="colourants" data-testid="tab-colourants">Colourant SKUs</TabsTrigger>
          <TabsTrigger value="pantones" data-testid="tab-pantones">Pantone Shades</TabsTrigger>
          <TabsTrigger value="vendor-mbs" data-testid="tab-vendor-mbs">Vendor MBs</TabsTrigger>
          <TabsTrigger value="awaiting-sourcing" data-testid="tab-awaiting-sourcing">Awaiting Sourcing</TabsTrigger>
          <TabsTrigger value="change-log" data-testid="tab-change-log">Change Log</TabsTrigger>
        </TabsList>

        <TabsContent value="composer" className="mt-4">
          <ColourwayMock />
        </TabsContent>

        <TabsContent value="colourants" className="mt-4">
          <ColourantSKUsTab />
        </TabsContent>

        <TabsContent value="pantones" className="mt-4">
          <PantoneShadesTab />
        </TabsContent>

        <TabsContent value="vendor-mbs" className="mt-4">
          <VendorMBsTab />
        </TabsContent>

        <TabsContent value="awaiting-sourcing" className="mt-4">
          <AwaitingSourcingTab />
        </TabsContent>

        <TabsContent value="change-log" className="mt-4">
          <ChangeLogTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, warning, subtitle }) {
  return (
    <Card className={warning ? "border-amber-300 bg-amber-50/40" : ""} data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`p-2 rounded-md ${warning ? "bg-amber-100 text-amber-700" : "bg-rose-50 text-rose-600"}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-2xl font-semibold tabular-nums">{value ?? "—"}</div>
          <div className="text-xs text-stone-600">{label}</div>
          {subtitle && <div className="text-[10px] text-stone-400 mt-0.5">{subtitle}</div>}
        </div>
      </CardContent>
    </Card>
  );
}

function AwaitingSourcingTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  // State for the per-row "Attach MB" dialog. Stores the colourant the
  // designer clicked on so the dialog opens pre-targeted.
  const [attachTarget, setAttachTarget] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/colourway/awaiting-sourcing`);
      setItems(data?.items || []);
    } catch (e) {
      toast.error("Failed to load awaiting-sourcing queue");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const filtered = useMemo(() => {
    const s = (search || "").trim().toLowerCase();
    if (!s) return items;
    return items.filter((it) =>
      (it.colourant_id || "").toLowerCase().includes(s) ||
      (it.pantone_code || "").toLowerCase().includes(s) ||
      (it.pantone_name || "").toLowerCase().includes(s) ||
      (it.part_id || "").toLowerCase().includes(s) ||
      (it.blocking_parts || []).some(
        (p) =>
          (p.part_name || "").toLowerCase().includes(s) ||
          (p.blocking_bidso_skus || []).some((b) => (b || "").toLowerCase().includes(s))
      )
    );
  }, [items, search]);

  return (
    <div className="space-y-4" data-testid="awaiting-sourcing-tab">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-end justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-base">Awaiting Sourcing</CardTitle>
              <CardDescription className="text-xs">
                Colourant SKUs with no Vendor MB attached <em>and</em> at least one INP/INM
                actively waiting on them. Each row lists the parts and Bidso SKUs blocked on it.
                Orphan Colourant SKUs (nobody waiting on them) are excluded — use the
                Database Updates page to purge them.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Search by Colourant / Pantone / Part / Bidso…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 w-72"
                data-testid="awaiting-sourcing-search"
              />
              <Button variant="outline" onClick={reload} data-testid="awaiting-sourcing-refresh">
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading && (
            <div className="py-10 text-center text-stone-500 text-sm">Loading…</div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="py-10 text-center text-stone-500 text-sm" data-testid="awaiting-sourcing-empty">
              {items.length === 0
                ? "Nothing awaiting sourcing — every Colourant SKU has at least one Vendor MB. 🎉"
                : "No rows match the current search."}
            </div>
          )}
          {!loading && filtered.length > 0 && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-44">Colourant SKU</TableHead>
                    <TableHead className="w-32">Pantone</TableHead>
                    <TableHead className="w-28">Application</TableHead>
                    <TableHead className="w-40">Polymer Base</TableHead>
                    <TableHead>Blocking Parts</TableHead>
                    <TableHead>Blocking Bidso SKUs</TableHead>
                    <TableHead className="w-32 text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((it) => (
                    <TableRow key={it.colourant_id} data-testid={`awaiting-sourcing-row-${it.colourant_id}`}>
                      <TableCell className="font-mono text-xs align-top">{it.colourant_id}</TableCell>
                      <TableCell className="align-top">
                        <div className="flex items-center gap-2">
                          {it.color_hex && (
                            <span
                              className="inline-block w-4 h-4 rounded border border-stone-300"
                              style={{ backgroundColor: it.color_hex }}
                              title={it.color_hex}
                            />
                          )}
                          <div>
                            <div className="font-mono text-xs">{it.pantone_code}</div>
                            {it.pantone_name && (
                              <div className="text-[10px] text-stone-500">{it.pantone_name}</div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs align-top">{it.application}</TableCell>
                      <TableCell className="font-mono text-[11px] align-top">{it.polymer_base}</TableCell>
                      <TableCell className="align-top">
                        {(it.blocking_parts || []).length === 0 ? (
                          <span className="text-xs text-stone-400">—</span>
                        ) : (
                          <div className="space-y-1">
                            {(it.blocking_parts || []).map((p) => (
                              <div key={p.rm_id} className="text-xs">
                                <span className="font-mono text-[11px]">{p.rm_id}</span>
                                <span className="ml-2 text-stone-600">{p.part_name}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="align-top">
                        {(() => {
                          const bidsos = Array.from(
                            new Set(
                              (it.blocking_parts || []).flatMap((p) => p.blocking_bidso_skus || [])
                            )
                          ).sort();
                          if (bidsos.length === 0) {
                            return <span className="text-xs text-stone-400">—</span>;
                          }
                          return (
                            <div className="flex flex-wrap gap-1">
                              {bidsos.map((b) => (
                                <span
                                  key={b}
                                  className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-800"
                                  data-testid={`awaiting-sourcing-bidso-${b}`}
                                >
                                  {b}
                                </span>
                              ))}
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="text-right align-top">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setAttachTarget(it)}
                          data-testid={`attach-existing-mb-btn-${it.colourant_id}`}
                        >
                          Attach MB
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {attachTarget && (
        <AttachExistingMBDialog
          target={attachTarget}
          onClose={() => setAttachTarget(null)}
          onAttached={() => { setAttachTarget(null); reload(); }}
        />
      )}
    </div>
  );
}


/* =========================================================================
 * AttachExistingMBDialog — picks an existing MB row (any state) and links
 * it to a colourant via POST /api/colourway/vendor-mbs/attach-existing.
 * Backend either attaches in-place (if source unattached) or clones the row
 * to a fresh rm_id and attaches the clone — so one physical MB can power
 * multiple colourants without touching the schema.
 * =======================================================================*/

function AttachExistingMBDialog({ target, onClose, onAttached }) {
  const [mbQuery, setMbQuery] = useState("");
  const [mbList, setMbList] = useState([]);
  const [mbLoading, setMbLoading] = useState(false);
  const [picked, setPicked] = useState(null);
  const [status, setStatus] = useState("APPROVED");
  const [submitting, setSubmitting] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState("");

  // Restrict the MB search to the application-compatible category only,
  // matching the backend guard (INJECTION → MB, POWDER_COAT/PAINT → PWD).
  const requiredCategory = target?.application === "INJECTION" ? "MB" : "PWD";

  useEffect(() => {
    const h = setTimeout(() => setDebouncedQuery((mbQuery || "").trim()), 250);
    return () => clearTimeout(h);
  }, [mbQuery]);

  useEffect(() => {
    if (debouncedQuery.length < 2) { setMbList([]); return; }
    (async () => {
      setMbLoading(true);
      try {
        const res = await axios.get(
          `${API}/raw-materials/search?q=${encodeURIComponent(debouncedQuery)}&category=${requiredCategory}&limit=20`,
        );
        // Backend may return {item_id, name} or {rm_id, rm_name} — normalise.
        const rows = (res.data?.items || []).map((r) => ({
          rm_id: r.item_id ?? r.rm_id,
          rm_name: r.rm_name || r.description || r.name || "",
          category: r.category || "",
          colourant_id: r.colourant_id || null,
          vendor_mb_status: r.vendor_mb_status || null,
        })).filter((r) => r.rm_id && r.category === requiredCategory);
        setMbList(rows);
      } catch (e) {
        setMbList([]);
      } finally {
        setMbLoading(false);
      }
    })();
  }, [debouncedQuery, requiredCategory]);

  const submit = async () => {
    if (!picked) { toast.error("Pick an MB first"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/colourway/vendor-mbs/attach-existing`, {
        source_rm_id: picked.rm_id,
        colourant_id: target.colourant_id,
        vendor_mb_status: status,
      });
      const action = res.data?.action;
      const newRmId = res.data?.rm_id || picked.rm_id;
      toast.success(
        action === "cloned_and_attached"
          ? `Cloned ${picked.rm_id} → ${newRmId} and attached to ${target.colourant_id}`
          : `Attached ${newRmId} to ${target.colourant_id}`,
        { duration: 6000 },
      );
      onAttached?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Attach failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl" data-testid="attach-existing-mb-dialog">
        <DialogHeader>
          <DialogTitle>Attach an existing MB to this colourant</DialogTitle>
          <DialogDescription asChild>
            <div className="text-xs space-y-0.5">
              <div>
                Target: <span className="font-mono">{target?.colourant_id}</span>
                {" · "}<span className="font-mono">{target?.pantone_code}</span>
                {" · "}{target?.application}{" · "}<span className="font-mono">{target?.polymer_base}</span>
              </div>
              <div className="text-stone-500">
                Pick any existing {requiredCategory} from the RM repository. If the MB is unattached it&apos;ll
                be attached directly; if it&apos;s already linked to another colourant the system will
                clone the row to a fresh rm_id and attach the clone. You cannot create a new MB here.
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-mono text-stone-600">Search {requiredCategory}</label>
            <Input
              autoFocus
              value={mbQuery}
              onChange={(e) => setMbQuery(e.target.value)}
              placeholder={`Type at least 2 characters (rm_id or description)…`}
              className="mt-1"
              data-testid="attach-existing-mb-search"
            />
          </div>

          <div className="max-h-64 overflow-y-auto border rounded-md" data-testid="attach-existing-mb-results">
            {mbLoading ? (
              <div className="py-6 text-center text-sm text-stone-500">Searching…</div>
            ) : debouncedQuery.length < 2 ? (
              <div className="py-6 text-center text-xs text-stone-400">Start typing to search</div>
            ) : mbList.length === 0 ? (
              <div className="py-6 text-center text-sm text-stone-500">No matching {requiredCategory} found.</div>
            ) : (
              <ul className="divide-y">
                {mbList.map((m) => {
                  const isPicked = picked?.rm_id === m.rm_id;
                  return (
                    <li
                      key={m.rm_id}
                      onClick={() => setPicked(m)}
                      className={`px-3 py-2 cursor-pointer flex items-center justify-between gap-2 ${isPicked ? "bg-violet-50 border-l-4 border-violet-500" : "hover:bg-stone-50"}`}
                      data-testid={`attach-existing-mb-result-${m.rm_id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs">{m.rm_id}</span>
                          <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-stone-100 border border-stone-200">{m.category}</span>
                          {m.colourant_id && (
                            <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-amber-100 border border-amber-200 text-amber-800" title="Already attached — clicking will clone this row">
                              attached · will clone
                            </span>
                          )}
                          {!m.colourant_id && (
                            <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 border border-emerald-200 text-emerald-800">
                              unattached · in-place
                            </span>
                          )}
                        </div>
                        <div className="text-sm mt-0.5 truncate text-stone-700">{m.rm_name}</div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider font-mono text-stone-600">Vendor MB status</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="mt-1" data-testid="attach-existing-mb-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="APPROVED">APPROVED</SelectItem>
                <SelectItem value="PREFERRED">PREFERRED (will demote others)</SelectItem>
                <SelectItem value="UNDER_TRIAL">UNDER_TRIAL</SelectItem>
                <SelectItem value="REJECTED">REJECTED</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={!picked || submitting}
            className="bg-violet-600 hover:bg-violet-700"
            data-testid="attach-existing-mb-submit"
          >
            {submitting ? "Attaching…" : (picked?.colourant_id ? "Clone & Attach" : "Attach")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* =========================================================================
 * Colourant ↔ Vendor MB tagging panel (Jun 2026)
 *
 * Inline drawer that opens beneath a Colourant SKU row, listing its
 * linked vendor MBs with status badges, a ★ PREFERRED radio toggle,
 * a per-MB Remove action, and an "+ Attach MB" picker.
 *
 * Strict invariants enforced visually:
 *   • At most one PREFERRED MB per colourant. The ★ is a RADIO — picking
 *     it on one MB calls the backend (which auto-demotes the previous
 *     preferred) and we reload.
 *   • Application × category compatibility is pre-filtered in the picker
 *     to avoid the backend's 400 ("INJECTION only takes MB", etc.).
 *
 * Mutating actions are gated to MASTER_ADMIN / TECH_OPS_ENGINEER;
 * everyone else gets a read-only view.
 * ======================================================================*/
function ColourantVendorMBPanel({ colourantId, application, onMBCountChange }) {
  const canWrite = useHasAnyRole(COLOURWAY_WRITE_ROLES);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyRm, setBusyRm] = useState(null);     // rm_id currently mutating
  const [attachOpen, setAttachOpen] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/colourway/colourant-skus/${colourantId}/vendor-mbs`,
      );
      setItems(data?.items || []);
      if (typeof onMBCountChange === "function") {
        onMBCountChange(colourantId, (data?.items || []).length);
      }
    } catch (e) {
      toast.error(backendErrorMessage(e, "Failed to load vendor MBs"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (colourantId) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colourantId]);

  const setPreferred = async (rm_id) => {
    setBusyRm(rm_id);
    try {
      await axios.put(`${API}/colourway/vendor-mbs/${rm_id}/status`, {
        vendor_mb_status: "PREFERRED",
      });
      toast.success(`${rm_id} → ★ PREFERRED`);
      await reload();   // server auto-demotes the previous one
    } catch (e) {
      toast.error(backendErrorMessage(e, "Could not set preferred"));
    } finally {
      setBusyRm(null);
    }
  };

  const changeStatus = async (rm_id, newStatus) => {
    setBusyRm(rm_id);
    try {
      await axios.put(`${API}/colourway/vendor-mbs/${rm_id}/status`, {
        vendor_mb_status: newStatus,
      });
      toast.success(`${rm_id} → ${newStatus}`);
      await reload();
    } catch (e) {
      toast.error(backendErrorMessage(e, "Status change failed"));
    } finally {
      setBusyRm(null);
    }
  };

  const detachOne = async (rm_id) => {
    if (!window.confirm(
      `Unlink ${rm_id} from colourant ${colourantId}?\n\n` +
      `If this MB powers other colourants those remain linked.`
    )) return;
    setBusyRm(rm_id);
    try {
      await axios.post(
        `${API}/colourway/vendor-mbs/${rm_id}/detach`,
        null,
        { params: { colourant_id: colourantId } },
      );
      toast.success(`${rm_id} unlinked`);
      await reload();
    } catch (e) {
      toast.error(backendErrorMessage(e, "Unlink failed"));
    } finally {
      setBusyRm(null);
    }
  };

  const preferredCount = items.filter((m) => m.vendor_mb_status === "PREFERRED").length;

  return (
    <div className="px-3 py-2 bg-stone-50 border-t" data-testid={`colourant-mb-panel-${colourantId}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wide font-semibold text-stone-600 flex items-center gap-2">
          <Beaker className="w-4 h-4" />
          Vendor MBs
          {loading && <span className="text-[10px] text-stone-400">loading…</span>}
          {!loading && items.length === 0 && (
            <span className="inline-flex items-center gap-1 text-amber-700 font-medium" data-testid={`colourant-no-mb-${colourantId}`}>
              <AlertCircle className="w-3.5 h-3.5" /> No MB attached — BOM rollup will skip the MB row.
            </span>
          )}
          {!loading && items.length > 0 && preferredCount === 0 && (
            <span className="inline-flex items-center gap-1 text-amber-700 font-medium" data-testid={`colourant-no-preferred-${colourantId}`}>
              <AlertCircle className="w-3.5 h-3.5" /> No PREFERRED MB — pick one with ★ to enable rollup.
            </span>
          )}
        </div>
        {canWrite && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setAttachOpen(true)}
            data-testid={`colourant-attach-mb-btn-${colourantId}`}
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Attach MB
          </Button>
        )}
      </div>

      {!loading && items.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-14">★</TableHead>
              <TableHead>RM ID</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Vendor(s)</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Latest ₹</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((mb) => {
              const isPref = mb.vendor_mb_status === "PREFERRED";
              const disabled = !canWrite || busyRm === mb.rm_id;
              return (
                <TableRow key={mb.rm_id} data-testid={`colourant-mb-row-${colourantId}-${mb.rm_id}`}>
                  <TableCell>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={isPref}
                      disabled={disabled || isPref}
                      onClick={() => setPreferred(mb.rm_id)}
                      className={`inline-flex items-center justify-center w-7 h-7 rounded-full border transition ${
                        isPref
                          ? "bg-emerald-600 border-emerald-700 text-white"
                          : "bg-white border-stone-300 text-stone-400 hover:text-amber-500 hover:border-amber-400"
                      } ${disabled && !isPref ? "opacity-40 cursor-not-allowed" : ""}`}
                      title={isPref ? "Already PREFERRED" : "Set as PREFERRED"}
                      data-testid={`colourant-mb-prefer-${colourantId}-${mb.rm_id}`}
                    >
                      <Star className={`w-3.5 h-3.5 ${isPref ? "fill-current" : ""}`} />
                    </button>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{mb.rm_id}</TableCell>
                  <TableCell className="max-w-xs truncate text-xs" title={mb.description}>{mb.description}</TableCell>
                  <TableCell className="text-xs">
                    {mb.vendors?.length > 0
                      ? mb.vendors.map((v) => v.name).join(", ")
                      : <span className="text-stone-400">—</span>}
                  </TableCell>
                  <TableCell>
                    {canWrite ? (
                      <Select
                        value={mb.vendor_mb_status || "APPROVED"}
                        disabled={disabled}
                        onValueChange={(v) => v === "PREFERRED" ? setPreferred(mb.rm_id) : changeStatus(mb.rm_id, v)}
                      >
                        <SelectTrigger
                          className="h-8 w-[120px]"
                          data-testid={`colourant-mb-status-${colourantId}-${mb.rm_id}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MB_STATUS_OPTIONS.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <MBStatusBadge status={mb.vendor_mb_status} />
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-xs">
                    {mb.latest_price != null ? `₹${Number(mb.latest_price).toFixed(2)}` : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    {canWrite && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                        disabled={disabled}
                        onClick={() => detachOne(mb.rm_id)}
                        data-testid={`colourant-mb-unlink-${colourantId}-${mb.rm_id}`}
                      >
                        <Link2Off className="w-3.5 h-3.5 mr-1" /> Remove
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {attachOpen && (
        <AttachMBToColourantDialog
          colourantId={colourantId}
          application={application}
          alreadyLinkedRmIds={new Set(items.map((m) => m.rm_id))}
          onClose={() => setAttachOpen(false)}
          onAttached={() => { setAttachOpen(false); reload(); }}
        />
      )}
    </div>
  );
}

/**
 * Picker dialog: searches `/colourway/vendor-mbs` (debounced) and
 * attaches the chosen one to a colourant via `/attach-existing`.
 *
 * Pre-filtered to the category required by the colourant's application
 * so the user never sees an incompatible row. Backend's hard rejection
 * is still surfaced verbatim as a toast if anything slips through.
 */
function AttachMBToColourantDialog({
  colourantId, application, alreadyLinkedRmIds, onClose, onAttached,
}) {
  const requiredCategory = requiredMBCategoryForApplication(application);
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);   // rm row
  const [status, setStatus] = useState("APPROVED");
  const [submitting, setSubmitting] = useState(false);
  // Feb 2026 — Phase 2 migration (defect A). reqId stale-guard so an
  // older debounced response can never overwrite a newer one under
  // throttled network.
  const reqIdRef = useRef(0);

  // Debounced search — backend `search=` is server-side regex.
  useEffect(() => {
    const t = setTimeout(async () => {
      const myReqId = ++reqIdRef.current;
      setSearching(true);
      try {
        const params = { limit: 50 };
        if (query.trim()) params.search = query.trim();
        if (requiredCategory) params.category = requiredCategory;
        const { data } = await axios.get(`${API}/colourway/vendor-mbs`, { params });
        if (myReqId !== reqIdRef.current) return;
        const filtered = (data?.items || []).filter((it) => !alreadyLinkedRmIds.has(it.rm_id));
        setItems(filtered);
      } catch (e) {
        if (myReqId !== reqIdRef.current) return;
        toast.error(backendErrorMessage(e, "Search failed"));
      } finally {
        if (myReqId === reqIdRef.current) setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, requiredCategory]);

  const submit = async () => {
    if (!picked) return toast.error("Pick an MB to attach");
    setSubmitting(true);
    try {
      await axios.post(`${API}/colourway/vendor-mbs/attach-existing`, {
        source_rm_id: picked.rm_id,
        colourant_id: colourantId,
        vendor_mb_status: status,
      });
      toast.success(`${picked.rm_id} linked to ${colourantId} (${status})`);
      onAttached();
    } catch (e) {
      // Backend returns 400 / 404 / 409 with descriptive `detail`. Show verbatim.
      toast.error(backendErrorMessage(e, "Attach failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid={`attach-mb-to-colourant-dialog-${colourantId}`}>
        <DialogHeader>
          <DialogTitle>Attach Vendor MB</DialogTitle>
          <DialogDescription>
            Linking to <span className="font-mono">{colourantId}</span>
            {application && (
              <> ({application}{requiredCategory && <> · category <span className="font-mono">{requiredCategory}</span></>})</>
            )}
            . Same MB can power multiple colourants — this just adds the link.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-stone-400" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search MB by RM ID / description / pantone…"
              className="pl-9"
              data-testid="attach-mb-search-input"
            />
          </div>

          <div className="border rounded max-h-64 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>RM ID</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Cat</TableHead>
                  <TableHead>Linked to</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {searching && (
                  <TableRow><TableCell colSpan={5} className="text-center text-xs text-stone-400 py-4">Searching…</TableCell></TableRow>
                )}
                {!searching && items.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center text-xs text-stone-500 py-4" data-testid="attach-mb-empty">
                    {query.trim() ? "No matches." : "Start typing to search."}
                  </TableCell></TableRow>
                )}
                {!searching && items.map((mb) => {
                  const linkedTo = (mb.colourant_ids && mb.colourant_ids.length) || (mb.colourant_id ? 1 : 0);
                  const isPicked = picked && picked.rm_id === mb.rm_id;
                  return (
                    <TableRow
                      key={mb.rm_id}
                      className={`cursor-pointer ${isPicked ? "bg-emerald-50" : "hover:bg-stone-50"}`}
                      onClick={() => setPicked(mb)}
                      data-testid={`attach-mb-row-${mb.rm_id}`}
                    >
                      <TableCell>
                        <input
                          type="radio"
                          checked={!!isPicked}
                          onChange={() => setPicked(mb)}
                          aria-label={`Pick ${mb.rm_id}`}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-xs">{mb.rm_id}</TableCell>
                      <TableCell className="max-w-md truncate text-xs" title={mb.description}>{mb.description}</TableCell>
                      <TableCell><Badge variant="secondary" className="text-[10px]">{mb.category}</Badge></TableCell>
                      <TableCell className="text-xs text-stone-600">
                        {linkedTo > 0 ? `${linkedTo} colourant${linkedTo === 1 ? "" : "s"}` : "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center gap-3">
            <Label className="text-xs">Status on attach</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-[160px]" data-testid="attach-mb-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MB_STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {status === "PREFERRED" && (
              <span className="text-[11px] text-amber-700">
                Will auto-demote any currently-preferred MB for this colourant.
              </span>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={!picked || submitting} data-testid="attach-mb-submit">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
            Attach
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* =========================================================================
 * Colourant SKUs tab
 * =======================================================================*/

function ColourantSKUsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filterApp, setFilterApp] = useState("__all__");
  const [createOpen, setCreateOpen] = useState(false);
  const [dedupeOpen, setDedupeOpen] = useState(false);
  const [autolinkOpen, setAutolinkOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  // Inline drawer: which colourant's vendor-MB panel is open?
  const [expandedId, setExpandedId] = useState(null);
  // Live mb count overrides — keeps the row badge in sync after panel
  // mutations without a full table reload.
  const [mbCountOverride, setMbCountOverride] = useState({});
  // Feb 2026 — Phase 2 migration (defect A). reqId stale-guard.
  const reqIdRef = useRef(0);

  const handleMBCountChange = (cid, n) => {
    setMbCountOverride((prev) => ({ ...prev, [cid]: n }));
  };

  const reload = async () => {
    const myReqId = ++reqIdRef.current;
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (search) params.search = search;
      if (filterApp && filterApp !== "__all__") params.application = filterApp;
      const { data } = await axios.get(`${API}/colourway/colourant-skus`, { params });
      if (myReqId !== reqIdRef.current) return;
      setItems(data.items || []);
    } catch (e) {
      if (myReqId !== reqIdRef.current) return;
      toast.error("Failed to load Colourant SKUs");
    } finally {
      if (myReqId === reqIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterApp]);

  return (
    <div className="space-y-4" data-testid="colourants-tab">
      {/* Search + Create */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-stone-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && reload()}
            placeholder="Search by colourant_id / pantone code / part…"
            className="pl-9"
            data-testid="colourants-search-input"
          />
        </div>
        <Select value={filterApp} onValueChange={setFilterApp}>
          <SelectTrigger className="w-[200px]" data-testid="colourants-filter-application">
            <SelectValue placeholder="Application" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Applications</SelectItem>
            {APPLICATIONS.map((a) => (
              <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={reload} data-testid="colourants-refresh-btn">
          Refresh
        </Button>
        <Button variant="outline" onClick={() => setDedupeOpen(true)} data-testid="colourants-dedupe-btn">
          Dedupe
        </Button>
        <Button variant="outline" onClick={() => setAutolinkOpen(true)} data-testid="colourants-autolink-btn">
          Auto-link MBs
        </Button>
        <Button onClick={() => setCreateOpen(true)} data-testid="colourants-create-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Colourant SKU
        </Button>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead>Colourant ID</TableHead>
                <TableHead>Pantone</TableHead>
                <TableHead>Polymer</TableHead>
                <TableHead>Application</TableHead>
                <TableHead className="text-right">Vendor MBs</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={8} className="text-center text-stone-500 py-8">Loading…</TableCell></TableRow>
              )}
              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-stone-500 py-10" data-testid="colourants-empty">
                    No Colourant SKUs yet. Click <em>New Colourant SKU</em> to add the first one.
                  </TableCell>
                </TableRow>
              )}
              {!loading && items.map((it) => {
                const expanded = expandedId === it.colourant_id;
                const liveCount = mbCountOverride[it.colourant_id];
                const mbCount = liveCount != null ? liveCount : it.vendor_mb_count;
                const hasMB = mbCount > 0;
                return (
                  <React.Fragment key={it.colourant_id}>
                    <TableRow data-testid={`colourant-row-${it.colourant_id}`}>
                      <TableCell>
                        <button
                          type="button"
                          aria-expanded={expanded}
                          aria-label={expanded ? "Collapse" : "Expand vendor MB panel"}
                          onClick={() => setExpandedId(expanded ? null : it.colourant_id)}
                          className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-stone-100"
                          data-testid={`colourant-expand-${it.colourant_id}`}
                        >
                          {expanded
                            ? <ChevronDown className="w-4 h-4" />
                            : <ChevronRight className="w-4 h-4" />}
                        </button>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{it.colourant_id}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <ColourSwatch hex={it.color_hex} />
                          <div className="leading-tight">
                            <div className="font-medium text-sm">{it.pantone_code}</div>
                            <div className="text-[11px] text-stone-500">{it.pantone_name || "—"}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{it.polymer_base || "—"}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{it.application}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {hasMB ? (
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                            {mbCount}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200" data-testid={`no-mb-warn-${it.colourant_id}`}>
                            <AlertCircle className="w-3 h-3 mr-1 inline" /> No vendor
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell><StatusBadge status={it.status} /></TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditing(it)}
                          data-testid={`colourant-edit-${it.colourant_id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                    {expanded && (
                      <TableRow data-testid={`colourant-expand-row-${it.colourant_id}`}>
                        <TableCell colSpan={8} className="p-0">
                          <ColourantVendorMBPanel
                            colourantId={it.colourant_id}
                            application={it.application}
                            onMBCountChange={handleMBCountChange}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <CreateColourantDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { setCreateOpen(false); reload(); }}
      />
      <DedupeColourantsDialog
        open={dedupeOpen}
        onClose={() => setDedupeOpen(false)}
        onComplete={() => { setDedupeOpen(false); reload(); }}
      />
      <AutolinkMBsDialog
        open={autolinkOpen}
        onClose={() => setAutolinkOpen(false)}
        onComplete={() => { reload(); }}
      />
      {editing && (
        <EditColourantDialog
          colourant={editing}
          onClose={() => setEditing(null)}
          onUpdated={() => { setEditing(null); reload(); }}
        />
      )}
    </div>
  );
}

/* =========================================================================
 * Dedupe Colourants Dialog (Phase B.3 — Feb 2026)
 * Drops `part_id` from Colourant SKU identity. Preview → Execute → Undo.
 * ========================================================================= */
function DedupeColourantsDialog({ open, onClose, onComplete }) {
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadPreview = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/colourway/colourants/dedupe-preview`);
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const loadRuns = async () => {
    try {
      const r = await axios.get(`${API}/colourway/colourants/dedupe-runs`);
      setRuns(r.data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (open) { loadPreview(); loadRuns(); setResult(null); }
  }, [open]);

  const execute = async () => {
    if (!preview || preview.merge_count === 0) {
      toast.info("Nothing to dedupe");
      return;
    }
    if (!window.confirm(
      `Merge ${preview.skus_to_merge_away} SKUs into ${preview.merge_count} canonical rows.\n` +
      `This is reversible via Undo. Proceed?`
    )) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/colourway/colourants/dedupe-execute`);
      setResult(r.data);
      toast.success(`Deduped: ${r.data.summary.skus_merged} merged, ${r.data.summary.skus_renamed} renamed`);
      await loadRuns();
      onComplete?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Execute failed");
    } finally {
      setBusy(false);
    }
  };

  const undo = async (runId) => {
    if (!window.confirm(`Undo dedupe run ${runId.slice(0, 8)}…?\nThis will restore all merged SKUs to ACTIVE.`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/colourway/colourants/dedupe-undo/${runId}`);
      toast.success(`Restored ${r.data.restored} SKUs`);
      await loadRuns();
      await loadPreview();
      onComplete?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Undo failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dedupe-colourants-dialog">
        <DialogHeader>
          <DialogTitle>Dedupe Colourant SKUs</DialogTitle>
          <DialogDescription>
            Merges legacy <code>CLR_PART_POLY_PANTONE_NNNN</code> rows into canonical
            <code> CLR_POLY_PANTONE_APP_NNNN</code> rows. Part is no longer part of the identity.
            Every dedupe run is reversible via Undo.
          </DialogDescription>
        </DialogHeader>

        {busy && <div className="py-4 text-center text-stone-500">Working…</div>}

        {preview && (
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-3">
              {[
                ["Total SKUs", preview.total_colourant_skus, "bg-slate-100"],
                ["Identity groups", preview.identity_groups, "bg-blue-50 text-blue-800"],
                ["Merge groups", preview.merge_count, "bg-amber-50 text-amber-800"],
                ["Will merge away", preview.skus_to_merge_away, "bg-rose-50 text-rose-800"],
              ].map(([k, v, cls]) => (
                <div key={k} className={`rounded p-3 ${cls}`}>
                  <div className="text-[11px] uppercase opacity-70">{k}</div>
                  <div className="text-xl font-bold">{v}</div>
                </div>
              ))}
            </div>

            {preview.merges?.length > 0 && (
              <div className="border rounded max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-stone-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2">Identity</th>
                      <th className="text-left p-2">Canonical (old id)</th>
                      <th className="text-right p-2">Losers</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.merges.slice(0, 50).map((m, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-2 font-mono">{m.polymer_base} × {m.pantone_code} × {m.application}</td>
                        <td className="p-2 font-mono text-emerald-700">{m.canonical_old_id}</td>
                        <td className="p-2 text-right">{m.losers.length}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <Button onClick={execute} disabled={busy || preview.merge_count === 0} className="w-full" data-testid="dedupe-execute-btn">
              {preview.merge_count === 0 ? "Nothing to dedupe" : `Execute (merge ${preview.skus_to_merge_away} SKUs)`}
            </Button>

            {result && (
              <div className="border rounded p-3 bg-emerald-50 text-sm">
                <div className="font-semibold mb-1">Run {result.run_id.slice(0, 8)}… complete</div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>Merged: <b>{result.summary.skus_merged}</b></div>
                  <div>Renamed: <b>{result.summary.skus_renamed}</b></div>
                  <div>RMs repointed: <b>{result.summary.raw_materials_repointed}</b></div>
                </div>
              </div>
            )}
          </div>
        )}

        {runs.length > 0 && (
          <div className="border-t pt-3 mt-3">
            <div className="text-xs font-semibold mb-2">Recent runs</div>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {runs.slice(0, 10).map((r) => (
                <div key={r.run_id} className="flex items-center justify-between text-xs border rounded p-2 bg-white">
                  <div>
                    <span className="font-mono">{r.run_id.slice(0, 8)}…</span>
                    <span className="ml-2 text-stone-500">{r.created_at}</span>
                    <span className="ml-2">merged={r.counts?.skus_merged}, renamed={r.counts?.skus_renamed}</span>
                    {r.undone && <Badge className="ml-2 bg-stone-200 text-stone-700" variant="secondary">UNDONE</Badge>}
                  </div>
                  {!r.undone && (
                    <Button size="sm" variant="ghost" onClick={() => undo(r.run_id)}>Undo</Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* =========================================================================
 * Auto-link MBs Dialog (Phase B.4 — Feb 2026)
 * For every MB with a pantone tag, infer polymer from BOM co-usage and link
 * to (or create) the matching Colourant SKU. Multi-polymer MBs get multiple
 * Colourant SKUs (Q15b). MBs with no polymer inference get a polymer-less
 * Colourant SKU (Q14a). Preview → Execute → Undo.
 * ========================================================================= */
function AutolinkMBsDialog({ open, onClose, onComplete }) {
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadPreview = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/colourway/colourants/autolink-preview`);
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const loadRuns = async () => {
    try {
      const r = await axios.get(`${API}/colourway/colourants/autolink-runs`);
      setRuns(r.data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (open) { loadPreview(); loadRuns(); setResult(null); }
  }, [open]);

  const execute = async () => {
    if (!preview || (preview.summary?.mbs_to_link || 0) === 0) {
      toast.info("Nothing to link");
      return;
    }
    if (!window.confirm(
      `Link ${preview.summary.mbs_to_link} MBs to Colourant SKUs ` +
      `(${preview.summary.colourants_to_create} new Colourant SKUs will be created, ` +
      `${preview.summary.colourants_reused} reused).\n\n` +
      `This is reversible via Undo. Proceed?`
    )) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/colourway/colourants/autolink-execute`);
      setResult(r.data);
      toast.success(`Linked ${r.data.mbs_modified} MBs`);
      await loadRuns();
      onComplete?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Execute failed");
    } finally {
      setBusy(false);
    }
  };

  const undo = async (runId) => {
    if (!window.confirm(`Undo auto-link run ${runId.slice(0, 8)}…?\nThis will restore prior MB colourant linkages.`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/colourway/colourants/autolink-undo/${runId}`);
      if (r.data.error) {
        toast.error(r.data.error);
      } else {
        toast.success(`Restored ${r.data.restored} MBs`);
      }
      await loadRuns();
      await loadPreview();
      onComplete?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Undo failed");
    } finally {
      setBusy(false);
    }
  };

  const s = preview?.summary || {};

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="autolink-mbs-dialog">
        <DialogHeader>
          <DialogTitle>Auto-link Vendor MBs to Colourant SKUs</DialogTitle>
          <DialogDescription>
            Scans every MB with a <code>pantone</code> tag, infers the polymer(s) it has co-occurred
            with in BOMs, and links the MB to the matching <code>polymer × pantone × INJECTION</code>
            {" "}Colourant SKU — creating new canonical Colourant SKUs as needed.
            Multi-polymer MBs get multiple Colourant SKUs. MBs with no polymer inference get a
            polymer-less Colourant SKU. Every run is reversible via Undo.
          </DialogDescription>
        </DialogHeader>

        {busy && <div className="py-4 text-center text-stone-500">Working…</div>}

        {preview && (
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-3">
              {[
                ["MBs scanned", s.mbs_scanned, "bg-slate-100"],
                ["MBs to link", s.mbs_to_link, "bg-amber-50 text-amber-800"],
                ["Already linked", s.mbs_already_linked, "bg-emerald-50 text-emerald-800"],
                ["Multi-polymer MBs", s.mbs_multi_polymer, "bg-blue-50 text-blue-800"],
              ].map(([k, v, cls]) => (
                <div key={k} className={`rounded p-3 ${cls}`}>
                  <div className="text-[11px] uppercase opacity-70">{k}</div>
                  <div className="text-xl font-bold">{v ?? 0}</div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-4 gap-3">
              {[
                ["Polymer inferred", s.mbs_inferred_polymer, "bg-slate-50"],
                ["No polymer (Q14a)", s.mbs_no_polymer, "bg-rose-50 text-rose-800"],
                ["Colourants to create", s.colourants_to_create, "bg-violet-50 text-violet-800"],
                ["Colourants reused", s.colourants_reused, "bg-slate-50"],
              ].map(([k, v, cls]) => (
                <div key={k} className={`rounded p-3 ${cls}`}>
                  <div className="text-[11px] uppercase opacity-70">{k}</div>
                  <div className="text-xl font-bold">{v ?? 0}</div>
                </div>
              ))}
            </div>

            {preview.actions?.length > 0 && (
              <div className="border rounded max-h-72 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-stone-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2">MB</th>
                      <th className="text-left p-2">Pantone</th>
                      <th className="text-left p-2">Polymers Inferred</th>
                      <th className="text-left p-2">Target Colourant IDs</th>
                      <th className="text-right p-2">To Add</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.actions.slice(0, 100).map((a, i) => (
                      <tr key={i} className="border-t" data-testid={`autolink-action-${a.rm_id}`}>
                        <td className="p-2 font-mono">
                          {a.rm_id}
                          <div className="text-[10px] text-stone-500 truncate max-w-[180px]" title={a.description}>{a.description}</div>
                        </td>
                        <td className="p-2 font-mono">{a.pantone_code}</td>
                        <td className="p-2 font-mono">
                          {(a.polymers_inferred || []).map(p => p || "—").join(", ") || "—"}
                        </td>
                        <td className="p-2 font-mono text-emerald-700">
                          {(a.target_colourant_ids || []).map((c, j) => (
                            <div key={j}>{c}</div>
                          ))}
                        </td>
                        <td className="p-2 text-right font-semibold">{(a.to_add || []).length}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {preview.actions_truncated && (
                  <div className="p-2 text-[11px] text-stone-500 bg-stone-50 border-t">
                    Showing first {preview.actions.length} actions (truncated).
                  </div>
                )}
              </div>
            )}

            <Button
              onClick={execute}
              disabled={busy || (s.mbs_to_link || 0) === 0}
              className="w-full"
              data-testid="autolink-execute-btn"
            >
              {(s.mbs_to_link || 0) === 0 ? "Nothing to link" : `Execute (link ${s.mbs_to_link} MBs)`}
            </Button>

            {result && (
              <div className="border rounded p-3 bg-emerald-50 text-sm" data-testid="autolink-result-banner">
                <div className="font-semibold mb-1">Run {result.run_id.slice(0, 8)}… complete</div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>MBs modified: <b>{result.mbs_modified}</b></div>
                  <div>Colourants created: <b>{result.summary?.colourants_to_create ?? 0}</b></div>
                  <div>Promoted to PREFERRED: <b>{result.summary?.promoted_to_preferred ?? 0}</b></div>
                </div>
              </div>
            )}
          </div>
        )}

        {runs.length > 0 && (
          <div className="border-t pt-3 mt-3">
            <div className="text-xs font-semibold mb-2">Recent runs</div>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {runs.slice(0, 10).map((r) => (
                <div key={r.run_id} className="flex items-center justify-between text-xs border rounded p-2 bg-white" data-testid={`autolink-run-${r.run_id}`}>
                  <div>
                    <span className="font-mono">{r.run_id.slice(0, 8)}…</span>
                    <span className="ml-2 text-stone-500">
                      {typeof r.created_at === "string" ? r.created_at : new Date(r.created_at).toLocaleString()}
                    </span>
                    <span className="ml-2">
                      mbs={r.counts?.mbs_to_link ?? 0}, new_clrs={r.counts?.colourants_to_create ?? 0}
                    </span>
                    {r.undone && <Badge className="ml-2 bg-stone-200 text-stone-700" variant="secondary">UNDONE</Badge>}
                  </div>
                  {!r.undone && (
                    <Button size="sm" variant="ghost" onClick={() => undo(r.run_id)} data-testid={`autolink-undo-${r.run_id}`}>Undo</Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* =========================================================================
 * Create Colourant dialog (with polymer auto-fill)
 * =======================================================================*/

function CreateColourantDialog({ open, onClose, onCreated }) {
  const [pantoneOptions, setPantoneOptions] = useState([]);
  const [polyOptions, setPolyOptions] = useState([]);
  const [partOptions, setPartOptions] = useState([]);
  const [form, setForm] = useState({
    pantone_code: "",
    part_id: "",
    polymer_base: "",
    application: "INJECTION",
    consumption_grams_per_unit: "",
    notes: "",
  });
  const [polySuggestion, setPolySuggestion] = useState(null);
  const [polyOverride, setPolyOverride] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [p, pl, pt] = await Promise.all([
          axios.get(`${API}/pantone/shades`, { params: { status: "ACTIVE", limit: 500 } }),
          axios.get(`${API}/raw-materials`, { params: { category: "POLY", limit: 200 } }),
          axios.get(`${API}/colourway/parts`),
        ]);
        setPantoneOptions(p.data?.shades || p.data?.items || []);
        const polys = pl.data?.items || pl.data?.raw_materials || pl.data || [];
        setPolyOptions(Array.isArray(polys) ? polys : []);
        setPartOptions(pt.data?.items || []);
      } catch (e) {
        toast.error("Failed to load reference data");
      }
    })();
    setForm({ pantone_code: "", part_id: "", polymer_base: "", application: "INJECTION", consumption_grams_per_unit: "", notes: "" });
    setPolySuggestion(null);
    setPolyOverride(false);
  }, [open]);

  // Polymer auto-fill — Phase B.3 (Feb 2026): polymer suggestion is now driven
  // by the user's manual selection rather than auto-derived from part. Until a
  // global polymer suggestion is built, we just rely on the manual dropdown.
  useEffect(() => {
    setPolySuggestion(null);
  }, [form.application]);

  // Application change clears polymer when not INJECTION
  useEffect(() => {
    if (form.application !== "INJECTION") {
      setForm((f) => ({ ...f, polymer_base: "" }));
    }
  }, [form.application]);

  const polymerLocked = useMemo(
    () => polySuggestion?.lock_level === "AUTO_FILL_LOCKED" && !polyOverride,
    [polySuggestion, polyOverride]
  );

  const submit = async () => {
    if (!form.pantone_code) return toast.error("Pick a Pantone shade");
    if (form.application === "INJECTION" && !form.polymer_base) {
      return toast.error("INJECTION colourants need a polymer base");
    }
    setSubmitting(true);
    try {
      const payload = {
        pantone_code: form.pantone_code,
        // part_id is deprecated (Phase B.3) — sent as empty for back-compat
        part_id: "",
        application: form.application,
        polymer_base: form.application === "INJECTION" ? form.polymer_base : null,
        consumption_grams_per_unit: form.consumption_grams_per_unit
          ? parseFloat(form.consumption_grams_per_unit)
          : null,
        notes: form.notes || null,
      };
      const { data } = await axios.post(`${API}/colourway/colourant-skus`, payload);
      toast.success(`Created ${data.colourant_id}`);
      onCreated();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "DUPLICATE_COLOURANT") {
        toast.warning(`Already exists as ${d.existing_colourant_id}`, { duration: 6000 });
      } else if (typeof d === "string") {
        toast.error(d);
      } else {
        toast.error(d?.message || "Create failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="create-colourant-dialog">
        <DialogHeader>
          <DialogTitle>New Colourant SKU</DialogTitle>
          <DialogDescription>
            A Colourant SKU = one Pantone shade applied to one part in one polymer.
            Vendor MBs get attached underneath in the next step.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 py-2">
          {/* Pantone */}
          <div className="col-span-2">
            <Label>Pantone Shade <span className="text-rose-500">*</span></Label>
            <Select value={form.pantone_code} onValueChange={(v) => setForm({ ...form, pantone_code: v })}>
              <SelectTrigger data-testid="create-colourant-pantone-trigger">
                <SelectValue placeholder="Pick a Pantone shade…" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                {pantoneOptions.map((p) => (
                  <SelectItem key={p.pantone_code} value={p.pantone_code} data-testid={`pantone-opt-${p.pantone_code}`}>
                    <div className="flex items-center gap-2">
                      <ColourSwatch hex={p.color_hex} />
                      <span className="font-medium">{p.pantone_code}</span>
                      <span className="text-stone-500 text-xs">— {p.pantone_name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Application */}
          <div>
            <Label>Application <span className="text-rose-500">*</span></Label>
            <Select value={form.application} onValueChange={(v) => setForm({ ...form, application: v })}>
              <SelectTrigger data-testid="create-colourant-application-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                {APPLICATIONS.map((a) => (
                  <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Part picker removed (Phase B.3 — Feb 2026)
              Colourant SKU identity is now (polymer_base, pantone_code, application).
              A single Colourant SKU is shared across all parts that use the same polymer + pantone. */}

          {/* Polymer (INJECTION only) */}
          {form.application === "INJECTION" && (
            <div className="col-span-2">
              <div className="flex items-center justify-between mb-1">
                <Label>Polymer Base <span className="text-rose-500">*</span></Label>
                {polymerLocked && (
                  <button
                    type="button"
                    className="text-xs text-rose-600 hover:underline"
                    onClick={() => setPolyOverride(true)}
                    data-testid="polymer-override-btn"
                  >
                    Override
                  </button>
                )}
              </div>
              <Select
                value={form.polymer_base}
                onValueChange={(v) => setForm({ ...form, polymer_base: v })}
                disabled={polymerLocked}
              >
                <SelectTrigger data-testid="create-colourant-polymer-trigger">
                  <SelectValue placeholder="Pick a polymer grade…" />
                </SelectTrigger>
                <SelectContent>
                  {polyOptions.map((p) => (
                    <SelectItem key={p.rm_id} value={p.rm_id}>
                      <span className="font-mono text-xs">{p.rm_id}</span>
                      <span className="ml-2 text-stone-600">{p.description}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {polySuggestion && polySuggestion.lock_level !== "MANUAL_ENTRY" && (
                <div
                  className={`text-xs mt-1 ${polySuggestion.lock_level === "AUTO_FILL_WARN" ? "text-amber-700" : "text-stone-500"}`}
                  data-testid="polymer-suggestion-hint"
                >
                  {polySuggestion.message}
                </div>
              )}
              {polySuggestion?.lock_level === "MANUAL_ENTRY" && (
                <div className="text-xs mt-1 text-stone-500" data-testid="polymer-suggestion-hint">
                  {polySuggestion.message}
                </div>
              )}
            </div>
          )}

          {/* Consumption + notes */}
          <div>
            <Label>Consumption (g/unit)</Label>
            <Input
              type="number"
              step="0.01"
              value={form.consumption_grams_per_unit}
              onChange={(e) => setForm({ ...form, consumption_grams_per_unit: e.target.value })}
              placeholder="Optional"
              data-testid="create-colourant-consumption"
            />
          </div>
          <div className="col-span-2">
            <Label>Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Any internal note…"
              rows={2}
              data-testid="create-colourant-notes"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="create-colourant-submit">
            {submitting ? "Creating…" : "Create Colourant SKU"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* =========================================================================
 * Edit Colourant dialog (limited fields per Phase 1 — status + notes + consumption)
 * =======================================================================*/

function EditColourantDialog({ colourant, onClose, onUpdated }) {
  const [form, setForm] = useState({
    status: colourant.status,
    consumption_grams_per_unit: colourant.consumption_grams_per_unit ?? "",
    notes: colourant.notes || "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.put(`${API}/colourway/colourant-skus/${colourant.colourant_id}`, {
        status: form.status,
        consumption_grams_per_unit: form.consumption_grams_per_unit
          ? parseFloat(form.consumption_grams_per_unit)
          : null,
        notes: form.notes || null,
      });
      toast.success(`Updated ${colourant.colourant_id}`);
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Update failed");
    } finally {
      setSubmitting(false);
    }
  };

  const softDelete = async () => {
    if (!window.confirm(`Deprecate ${colourant.colourant_id}?`)) return;
    try {
      await axios.delete(`${API}/colourway/colourant-skus/${colourant.colourant_id}`);
      toast.success("Deprecated");
      onUpdated();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d?.message || d || "Delete failed");
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="edit-colourant-dialog">
        <DialogHeader>
          <DialogTitle>Edit {colourant.colourant_id}</DialogTitle>
          <DialogDescription>
            Pantone, Part, Polymer and Application are immutable. To swap any
            of those, deprecate this row and create a new one.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Field label="Pantone" value={`${colourant.pantone_code} — ${colourant.pantone_name || ""}`} />
            {colourant.part_id && <Field label="Part (legacy)" value={colourant.part_id} />}
            <Field label="Polymer" value={colourant.polymer_base || "—"} mono />
            <Field label="Application" value={colourant.application} />
          </div>

          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="edit-colourant-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ACTIVE">ACTIVE</SelectItem>
                <SelectItem value="DRAFT">DRAFT</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Consumption (g/unit)</Label>
            <Input
              type="number"
              step="0.01"
              value={form.consumption_grams_per_unit}
              onChange={(e) => setForm({ ...form, consumption_grams_per_unit: e.target.value })}
            />
          </div>

          <div>
            <Label>Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter className="flex justify-between sm:justify-between">
          <Button variant="destructive" onClick={softDelete} data-testid="edit-colourant-deprecate">
            <Trash2 className="w-4 h-4 mr-2" /> Deprecate
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={submit} disabled={submitting} data-testid="edit-colourant-save">
              {submitting ? "Saving…" : "Save"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-[11px] text-stone-500 uppercase tracking-wide">{label}</div>
      <div className={`text-sm ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}

/* =========================================================================
 * Pantone Shades tab — read-only browse (mgmt at Tech Ops → Pantone)
 * =======================================================================*/

function PantoneShadesTab() {
  const [shades, setShades] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloadingTpl, setDownloadingTpl] = useState(false);
  const fileInputRef = useRef(null);
  // Feb 2026 — Phase 2 migration (defect A). reqId stale-guard.
  const reqIdRef = useRef(0);

  const reload = async () => {
    const myReqId = ++reqIdRef.current;
    setLoading(true);
    try {
      const params = { status: "ACTIVE", limit: 500 };
      if (search) params.search = search;
      const { data } = await axios.get(`${API}/pantone/shades`, { params });
      if (myReqId !== reqIdRef.current) return;
      setShades(data?.shades || data?.items || []);
    } catch (e) {
      if (myReqId !== reqIdRef.current) return;
      toast.error("Failed to load Pantone shades");
    } finally {
      if (myReqId === reqIdRef.current) setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const downloadTemplate = async () => {
    setDownloadingTpl(true);
    try {
      const res = await axios.get(`${API}/pantone/shades/download-template`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "pantone_shades_template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to download template");
    } finally {
      setDownloadingTpl(false);
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/pantone/shades/bulk-import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const r = res.data || {};
      const c = r.shades_created || 0;
      const u = r.shades_updated || 0;
      const errs = (r.errors || []).length;
      if (errs > 0) {
        toast.warning(`Imported ${c} new, updated ${u}. ${errs} row(s) had errors — check the response.`, { duration: 8000 });
        // eslint-disable-next-line no-console
        console.warn("Pantone import errors:", r.errors);
      } else {
        toast.success(`Imported ${c} new, updated ${u} Pantone shade(s).`);
      }
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk import failed");
    } finally {
      setUploading(false);
    }
  };

  const exportXlsx = async () => {
    try {
      const res = await axios.get(`${API}/pantone/shades/export`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pantone_shades_${Date.now()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="pantones-tab">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-stone-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && reload()}
            placeholder="Search Pantone code / name…"
            className="pl-9"
            data-testid="pantones-search-input"
          />
        </div>
        <Button variant="outline" onClick={reload} data-testid="pantones-refresh-btn">Refresh</Button>
        <Button
          variant="outline"
          onClick={downloadTemplate}
          disabled={downloadingTpl}
          data-testid="pantones-download-template-btn"
        >
          {downloadingTpl ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
          Template
        </Button>
        <Button
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          data-testid="pantones-bulk-upload-btn"
        >
          {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
          {uploading ? "Importing…" : "Bulk Upload"}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          className="hidden"
          onChange={handleBulkUpload}
          data-testid="pantones-file-input"
        />
        <Button variant="outline" onClick={exportXlsx} data-testid="pantones-export-btn">
          <Download className="w-4 h-4 mr-1" />
          Export
        </Button>
        <Button
          className="bg-rose-600 hover:bg-rose-700"
          onClick={() => setAddOpen(true)}
          data-testid="pantones-add-btn"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Shade
        </Button>
      </div>

      <AddPantoneShadeDialog open={addOpen} onOpenChange={setAddOpen} onSaved={reload} />

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Swatch</TableHead>
                <TableHead>Pantone Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Family</TableHead>
                <TableHead>Hex</TableHead>
                <TableHead>Categories</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={6} className="text-center py-8 text-stone-500">Loading…</TableCell></TableRow>}
              {!loading && shades.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-stone-500">No shades.</TableCell></TableRow>
              )}
              {!loading && shades.map((s) => (
                <TableRow key={s.pantone_code} data-testid={`pantone-row-${s.pantone_code}`}>
                  <TableCell>
                    {s.color_hex ? (
                      <span
                        className="inline-block h-7 rounded-sm border border-stone-300 align-middle"
                        style={{ width: "2cm", background: s.color_hex }}
                        title={s.color_hex}
                        data-testid={`pantone-swatch-${s.pantone_code}`}
                      />
                    ) : (
                      <span
                        className="inline-block h-7 rounded-sm border border-stone-300 bg-stone-100 align-middle"
                        style={{ width: "2cm" }}
                        data-testid={`pantone-swatch-empty-${s.pantone_code}`}
                      />
                    )}
                  </TableCell>
                  <TableCell className="font-medium">{s.pantone_code}</TableCell>
                  <TableCell>{s.pantone_name}</TableCell>
                  <TableCell><Badge variant="secondary">{s.color_family}</Badge></TableCell>
                  <TableCell className="font-mono text-xs">{s.color_hex}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(s.applicable_categories || []).map((c) => (
                        <Badge key={c} variant="outline" className="text-[10px]">{c}</Badge>
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}


const COLOR_FAMILIES = [
  "RED", "ORANGE", "YELLOW", "GREEN", "BLUE",
  "PURPLE", "PINK", "BROWN", "BLACK", "WHITE", "OTHER",
];

const PANTONE_CATEGORY_OPTIONS = ["INP", "INM", "INMFAB", "ACC", "BS", "PM", "SP", "LB"];

function AddPantoneShadeDialog({ open, onOpenChange, onSaved }) {
  const empty = {
    pantone_code: "",
    pantone_name: "",
    color_hex: "#",
    color_family: "OTHER",
    applicable_categories: ["INP", "INM", "ACC"],
    notes: "",
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm(empty); /* eslint-disable-next-line */ }, [open]);

  const toggleCategory = (c) => {
    setForm((f) => ({
      ...f,
      applicable_categories: f.applicable_categories.includes(c)
        ? f.applicable_categories.filter((x) => x !== c)
        : [...f.applicable_categories, c],
    }));
  };

  const canSave =
    form.pantone_code.trim() &&
    form.pantone_name.trim() &&
    /^#?[0-9a-fA-F]{6}$/.test(form.color_hex.replace(/^#/, "")) &&
    form.applicable_categories.length > 0;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        pantone_code: form.pantone_code.trim().toUpperCase(),
        pantone_name: form.pantone_name.trim(),
        color_hex: form.color_hex.startsWith("#") ? form.color_hex : `#${form.color_hex}`,
        color_family: form.color_family,
        applicable_categories: form.applicable_categories,
        notes: form.notes || "",
      };
      await axios.post(`${API}/pantone/shades`, payload);
      toast.success(`Created Pantone ${payload.pantone_code}`);
      onSaved && onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create Pantone shade");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-pantone-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Pantone Shade</DialogTitle>
          <DialogDescription>
            Define one Pantone shade. For bulk uploads use the Bulk Upload button.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Pantone Code *</Label>
              <Input
                value={form.pantone_code}
                onChange={(e) => setForm({ ...form, pantone_code: e.target.value })}
                placeholder="e.g. 485 C"
                data-testid="add-pantone-code"
              />
            </div>
            <div>
              <Label className="text-xs">Family *</Label>
              <Select
                value={form.color_family}
                onValueChange={(v) => setForm({ ...form, color_family: v })}
              >
                <SelectTrigger data-testid="add-pantone-family"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COLOR_FAMILIES.map((f) => (
                    <SelectItem key={f} value={f}>{f}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Name *</Label>
            <Input
              value={form.pantone_name}
              onChange={(e) => setForm({ ...form, pantone_name: e.target.value })}
              placeholder="e.g. Bright Red"
              data-testid="add-pantone-name"
            />
          </div>
          <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <Label className="text-xs">Hex *</Label>
              <Input
                value={form.color_hex}
                onChange={(e) => setForm({ ...form, color_hex: e.target.value })}
                placeholder="#DA291C"
                className="font-mono"
                data-testid="add-pantone-hex"
              />
            </div>
            <span
              className="inline-block rounded-sm border border-stone-300"
              style={{
                width: "2cm",
                height: "1.8rem",
                background: /^#?[0-9a-fA-F]{6}$/.test(form.color_hex.replace(/^#/, ""))
                  ? (form.color_hex.startsWith("#") ? form.color_hex : `#${form.color_hex}`)
                  : "transparent",
              }}
            />
          </div>
          <div>
            <Label className="text-xs">Applicable Categories *</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {PANTONE_CATEGORY_OPTIONS.map((c) => {
                const selected = form.applicable_categories.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => toggleCategory(c)}
                    className={`px-2 py-1 text-xs rounded border ${
                      selected
                        ? "bg-rose-100 border-rose-400 text-rose-800"
                        : "bg-white border-stone-300 text-stone-600 hover:bg-stone-50"
                    }`}
                    data-testid={`add-pantone-cat-${c}`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button
            className="bg-rose-600 hover:bg-rose-700"
            onClick={save}
            disabled={!canSave || saving}
            data-testid="add-pantone-save"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



/* =========================================================================
 * Vendor MBs tab — Phase 2
 *   Lists every MB / PWD with its Colourant linkage. Lets procurement
 *   attach an MB to a Colourant SKU, change status, set preferred.
 * =======================================================================*/

const MB_STATUS_OPTIONS = ["PREFERRED", "APPROVED", "TRIAL", "BLOCKED"];

// Roles allowed to mutate Colourway data (matches existing Colourway gating)
const COLOURWAY_WRITE_ROLES = [
  "MASTER_ADMIN",
  "master_admin",
  "TECH_OPS_ENGINEER",
];

/** Extract a human-readable error from an axios error, falling back to a default. */
function backendErrorMessage(e, fallback = "Request failed") {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object" && d.message) return d.message;
  if (e?.message) return e.message;
  return fallback;
}

/**
 * Backend's `/colourway/vendor-mbs/attach-existing` rejects mismatched
 * application×category pairs with a 400 (e.g. INJECTION + PWD). We
 * mirror that rule client-side to pre-filter the picker so the user
 * doesn't get a toast for an obvious mistake.
 *
 * INJECTION → MB; POWDER_COAT/PAINT → PWD.
 */
function requiredMBCategoryForApplication(application) {
  if (application === "INJECTION") return "MB";
  if (application === "POWDER_COAT" || application === "PAINT") return "PWD";
  return null;  // unknown application → no pre-filter
}

function MBStatusBadge({ status }) {
  if (!status) {
    return <Badge variant="outline" className="bg-stone-100 text-stone-600">Unlinked</Badge>;
  }
  const v = {
    PREFERRED: "bg-emerald-600 text-white",
    APPROVED:  "bg-emerald-100 text-emerald-800 border-emerald-200",
    TRIAL:     "bg-amber-100 text-amber-800 border-amber-200",
    BLOCKED:   "bg-rose-100 text-rose-800 border-rose-200",
  };
  return (
    <Badge variant="outline" className={v[status] || ""} data-testid={`mb-status-${status}`}>
      {status === "PREFERRED" ? "★ PREFERRED" : status}
    </Badge>
  );
}

function InlinePantonePicker({ currentCode, shades, onPick, testid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const current = currentCode
    ? shades.find((s) => s.pantone_code === currentCode)
    : null;

  const filtered = !q
    ? shades.slice(0, 200)
    : shades
        .filter(
          (s) =>
            (s.pantone_code || "").toLowerCase().includes(q.toLowerCase()) ||
            (s.pantone_name || "").toLowerCase().includes(q.toLowerCase()),
        )
        .slice(0, 200);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-2 px-2 py-1 rounded border text-xs hover:bg-stone-50 ${
            currentCode
              ? "border-emerald-300 bg-emerald-50 text-emerald-800"
              : "border-amber-300 bg-amber-50 text-amber-800"
          }`}
          data-testid={testid}
        >
          {current?.color_hex && (
            <span
              className="inline-block w-3 h-3 rounded-sm border border-stone-300"
              style={{ background: current.color_hex }}
            />
          )}
          <span className="font-mono">
            {currentCode || "Tag Pantone…"}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <Command>
          <CommandInput
            placeholder="Search code or name…"
            value={q}
            onValueChange={setQ}
            data-testid={`${testid}-search`}
          />
          <CommandList>
            <CommandEmpty>No matching Pantone.</CommandEmpty>
            <CommandGroup>
              {currentCode && (
                <CommandItem
                  value="__clear__"
                  onSelect={() => { setOpen(false); onPick(""); }}
                  className="text-rose-700"
                  data-testid={`${testid}-clear`}
                >
                  ✕ Clear Pantone tag
                </CommandItem>
              )}
              {filtered.map((s) => (
                <CommandItem
                  key={s.pantone_code}
                  value={`${s.pantone_code} ${s.pantone_name || ""}`}
                  onSelect={() => { setOpen(false); onPick(s.pantone_code); }}
                  data-testid={`${testid}-opt-${s.pantone_code}`}
                >
                  <span
                    className="inline-block w-4 h-4 rounded-sm border border-stone-300 mr-2"
                    style={{ background: s.color_hex || "#e5e7eb" }}
                  />
                  <span className="font-mono mr-2">{s.pantone_code}</span>
                  <span className="text-stone-600">{s.pantone_name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function VendorMBsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("__all__");
  const [linkage, setLinkage] = useState("__all__"); // __all__ | linked | unlinked
  const [pantoneFilter, setPantoneFilter] = useState("__all__"); // __all__ | tagged | untagged
  const [attachOpen, setAttachOpen] = useState(false);
  const [attachTarget, setAttachTarget] = useState(null);
  const [pantoneShades, setPantoneShades] = useState([]); // master library
  // Feb 2026 — Phase 2 migration (defect A). reqId stale-guard.
  const reqIdRef = useRef(0);

  // Load Pantone shades once for the inline picker
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/pantone/shades`, {
          params: { status: "ACTIVE", limit: 1000 },
        });
        setPantoneShades(data?.shades || data?.items || []);
      } catch (_e) { /* non-fatal */ }
    })();
  }, []);

  const reload = async () => {
    const myReqId = ++reqIdRef.current;
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (search) params.search = search;
      if (category !== "__all__") params.category = category;
      if (linkage === "unlinked") params.unlinked_only = true;
      const { data } = await axios.get(`${API}/colourway/vendor-mbs`, { params });
      if (myReqId !== reqIdRef.current) return;
      let rows = data.items || [];
      if (linkage === "linked") rows = rows.filter((r) => r.colourant_id);
      if (pantoneFilter === "untagged") rows = rows.filter((r) => !r.pantone);
      if (pantoneFilter === "tagged") rows = rows.filter((r) => !!r.pantone);
      setItems(rows);
    } catch (e) {
      if (myReqId !== reqIdRef.current) return;
      toast.error("Failed to load Vendor MBs");
    } finally {
      if (myReqId === reqIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, linkage, pantoneFilter]);

  const tagPantone = async (rm_id, pantone_code) => {
    try {
      await axios.put(`${API}/colourway/vendor-mb-rm/${rm_id}/pantone`, {
        pantone_code: pantone_code || null,
      });
      toast.success(pantone_code ? `${rm_id} → Pantone ${pantone_code}` : `${rm_id} Pantone cleared`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Pantone tag failed");
    }
  };

  const changeStatus = async (rm_id, newStatus) => {
    try {
      await axios.put(`${API}/colourway/vendor-mbs/${rm_id}/status`, {
        vendor_mb_status: newStatus,
      });
      toast.success(`${rm_id} → ${newStatus}`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Status change failed");
    }
  };

  const detach = async (rm_id) => {
    if (!window.confirm(`Detach ${rm_id} from its Colourant SKU?`)) return;
    try {
      await axios.post(`${API}/colourway/vendor-mbs/${rm_id}/detach`);
      toast.success(`${rm_id} detached`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Detach failed");
    }
  };

  // Unlink ONE colourant from an MB while leaving other links intact.
  const detachOneChip = async (rm_id, colourant_id) => {
    if (!window.confirm(
      `Unlink colourant ${colourant_id} from ${rm_id}?\n\n` +
      `Other colourants this MB powers stay linked.`
    )) return;
    try {
      await axios.post(
        `${API}/colourway/vendor-mbs/${rm_id}/detach`,
        null,
        { params: { colourant_id } },
      );
      toast.success(`${colourant_id} unlinked from ${rm_id}`);
      reload();
    } catch (e) {
      toast.error(backendErrorMessage(e, "Unlink failed"));
    }
  };

  // Full reset — strip every link + clear status. Backend's
  // `/detach` with no colourant_id parameter does exactly this.
  const resetAllLinks = async (rm_id) => {
    if (!window.confirm(
      `RESET ${rm_id}?\n\nRemoves ALL colourant links and clears its ` +
      `vendor_mb_status. Use only when re-classifying the MB from scratch.`
    )) return;
    try {
      await axios.post(`${API}/colourway/vendor-mbs/${rm_id}/detach`);
      toast.success(`${rm_id} reset (all links cleared)`);
      reload();
    } catch (e) {
      toast.error(backendErrorMessage(e, "Reset failed"));
    }
  };

  // Open a colourant picker to add ONE more link to this MB.
  const [linkPickerFor, setLinkPickerFor] = useState(null);   // mb row

  return (
    <div className="space-y-4" data-testid="vendor-mbs-tab">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-stone-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && reload()}
            placeholder="Search RM ID / description / Pantone…"
            className="pl-9"
            data-testid="vendor-mbs-search-input"
          />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-[160px]" data-testid="vendor-mbs-filter-category">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Categories</SelectItem>
            <SelectItem value="MB">MB (Plastic)</SelectItem>
            <SelectItem value="PWD">PWD (Powder)</SelectItem>
          </SelectContent>
        </Select>
        <Select value={linkage} onValueChange={setLinkage}>
          <SelectTrigger className="w-[160px]" data-testid="vendor-mbs-filter-linkage">
            <SelectValue placeholder="Linkage" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All</SelectItem>
            <SelectItem value="linked">Linked only</SelectItem>
            <SelectItem value="unlinked">Unlinked only</SelectItem>
          </SelectContent>
        </Select>
        <Select value={pantoneFilter} onValueChange={setPantoneFilter}>
          <SelectTrigger className="w-[170px]" data-testid="vendor-mbs-filter-pantone">
            <SelectValue placeholder="Pantone tag" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Any Pantone</SelectItem>
            <SelectItem value="tagged">Tagged only</SelectItem>
            <SelectItem value="untagged">Untagged only</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={reload} data-testid="vendor-mbs-refresh-btn">Refresh</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>RM ID</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Cat</TableHead>
                <TableHead>Pantone</TableHead>
                <TableHead>Linked Colourants</TableHead>
                <TableHead>Vendor(s)</TableHead>
                <TableHead className="text-right">Latest ₹</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={9} className="text-center py-8 text-stone-500">Loading…</TableCell></TableRow>}
              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-10 text-stone-500" data-testid="vendor-mbs-empty">
                    No Vendor MBs match. Try widening the filters.
                  </TableCell>
                </TableRow>
              )}
              {!loading && items.map((it) => (
                <TableRow key={it.rm_id} data-testid={`vendor-mb-row-${it.rm_id}`}>
                  <TableCell className="font-mono text-xs">{it.rm_id}</TableCell>
                  <TableCell className="max-w-xs truncate" title={it.description}>{it.description}</TableCell>
                  <TableCell><Badge variant="secondary" className="text-[10px]">{it.category}</Badge></TableCell>
                  <TableCell>
                    <InlinePantonePicker
                      currentCode={it.pantone || ""}
                      shades={pantoneShades}
                      onPick={(code) => tagPantone(it.rm_id, code)}
                      testid={`mb-pantone-picker-${it.rm_id}`}
                    />
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {(() => {
                      // Show every linked colourant as a chip with a
                      // per-chip × unlink. Falls back to the legacy
                      // scalar `colourant_id` if the array is empty.
                      const cids = (it.colourant_ids && it.colourant_ids.length
                        ? it.colourant_ids
                        : (it.colourant_id ? [it.colourant_id] : []));
                      if (cids.length === 0) {
                        return <span className="text-amber-600">—</span>;
                      }
                      return (
                        <div className="flex flex-wrap gap-1" data-testid={`mb-cids-${it.rm_id}`}>
                          {cids.map((cid) => (
                            <span
                              key={cid}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-emerald-200 bg-emerald-50 text-emerald-800 text-[11px] font-mono"
                              data-testid={`mb-cid-chip-${it.rm_id}-${cid}`}
                            >
                              {cid}
                              <button
                                type="button"
                                onClick={() => detachOneChip(it.rm_id, cid)}
                                className="hover:text-rose-700"
                                title="Unlink this colourant"
                                aria-label={`Unlink ${cid}`}
                                data-testid={`mb-cid-chip-unlink-${it.rm_id}-${cid}`}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="text-xs">
                    {it.vendors?.length > 0 ? it.vendors.map((v) => v.name).join(", ") : <span className="text-stone-400">—</span>}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-xs">
                    {it.latest_price != null ? `₹${Number(it.latest_price).toFixed(2)}` : "—"}
                  </TableCell>
                  <TableCell>
                    {it.colourant_id ? (
                      <Select
                        value={it.vendor_mb_status || "APPROVED"}
                        onValueChange={(v) => changeStatus(it.rm_id, v)}
                      >
                        <SelectTrigger className="h-8 w-[130px]" data-testid={`mb-status-trigger-${it.rm_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MB_STATUS_OPTIONS.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <MBStatusBadge status={null} />
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button
                        variant="outline" size="sm"
                        onClick={() => setLinkPickerFor(it)}
                        data-testid={`mb-link-colourant-btn-${it.rm_id}`}
                        title="Add another colourant link"
                      >
                        <Link2 className="w-3.5 h-3.5 mr-1" /> Link to colourant
                      </Button>
                      {(it.colourant_id || (it.colourant_ids?.length ?? 0) > 0) && (
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => resetAllLinks(it.rm_id)}
                          data-testid={`mb-reset-btn-${it.rm_id}`}
                          className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                          title="Clear all links + status (full reset)"
                        >
                          Reset
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {attachOpen && (
        <AttachMBDialog
          mb={attachTarget}
          onClose={() => { setAttachOpen(false); setAttachTarget(null); }}
          onAttached={() => { setAttachOpen(false); setAttachTarget(null); reload(); }}
        />
      )}

      {linkPickerFor && (
        <LinkMBToColourantDialog
          mb={linkPickerFor}
          onClose={() => setLinkPickerFor(null)}
          onAttached={() => { setLinkPickerFor(null); reload(); }}
        />
      )}
    </div>
  );
}

function AttachMBDialog({ mb, onClose, onAttached }) {
  const [colourants, setColourants] = useState([]);
  const [colourantId, setColourantId] = useState("");
  const [status, setStatus] = useState("APPROVED");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      // Only show colourants that match the MB's category
      const requiredApp = mb.category === "PWD" ? ["POWDER_COAT", "PAINT"] : ["INJECTION"];
      try {
        // No single multi-app filter; fetch all ACTIVE and filter client-side
        const { data } = await axios.get(`${API}/colourway/colourant-skus`, {
          params: { status: "ACTIVE", limit: 500 },
        });
        const filtered = (data.items || []).filter((c) => requiredApp.includes(c.application));
        setColourants(filtered);
      } catch (e) {
        toast.error("Failed to load Colourant SKUs");
      }
    })();
  }, [mb]);

  const submit = async () => {
    if (!colourantId) return toast.error("Pick a Colourant SKU");
    setSubmitting(true);
    try {
      await axios.post(`${API}/colourway/vendor-mbs/attach`, {
        rm_id: mb.rm_id,
        colourant_id: colourantId,
        vendor_mb_status: status,
      });
      toast.success(`${mb.rm_id} → ${colourantId}`);
      onAttached();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Attach failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="attach-mb-dialog">
        <DialogHeader>
          <DialogTitle>Attach {mb.rm_id} to a Colourant SKU</DialogTitle>
          <DialogDescription>
            Pick the Colourant SKU this {mb.category === "PWD" ? "powder" : "master batch"} should
            roll up to. Procurement decisions (preferred / blocked) live here; designers don't see this.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Field label="RM ID" value={mb.rm_id} mono />
            <Field label="Category" value={mb.category} />
            <Field label="Description" value={mb.description} />
            <Field label="Pantone (legacy)" value={mb.pantone || "—"} />
          </div>

          <div>
            <Label>Colourant SKU <span className="text-rose-500">*</span></Label>
            <Select value={colourantId} onValueChange={setColourantId}>
              <SelectTrigger data-testid="attach-mb-colourant-trigger">
                <SelectValue placeholder="Pick a Colourant SKU…" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                {colourants.map((c) => (
                  <SelectItem key={c.colourant_id} value={c.colourant_id}>
                    <div className="flex items-center gap-2">
                      <ColourSwatch hex={c.color_hex} />
                      <span className="font-mono text-xs">{c.colourant_id}</span>
                      <span className="text-stone-500 text-xs">— {c.pantone_code}{c.part_id ? ` · ${c.part_id}` : ""}</span>
                    </div>
                  </SelectItem>
                ))}
                {colourants.length === 0 && (
                  <div className="px-3 py-2 text-sm text-stone-500">
                    No matching Colourant SKUs. Create one first (correct application: {mb.category === "PWD" ? "POWDER_COAT" : "INJECTION"}).
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Initial Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="attach-mb-status-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MB_STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-stone-500 mt-1">
              PREFERRED demotes the existing preferred MB (if any) for this Colourant SKU to APPROVED.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="attach-mb-submit">
            {submitting ? "Attaching…" : "Attach"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * MB-side picker (mirror of AttachMBToColourantDialog). Picks a
 * Colourant SKU and ADDs it to the MB's `colourant_ids` array via
 * `/vendor-mbs/attach-existing`. Pre-filters by application×category
 * compatibility so the user never sees a mismatched colourant.
 */
function LinkMBToColourantDialog({ mb, onClose, onAttached }) {
  const requiredApps = mb.category === "PWD" ? ["POWDER_COAT", "PAINT"] : ["INJECTION"];
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(null);
  const [status, setStatus] = useState("APPROVED");
  const [submitting, setSubmitting] = useState(false);

  const alreadyLinked = new Set(
    (mb.colourant_ids && mb.colourant_ids.length)
      ? mb.colourant_ids
      : (mb.colourant_id ? [mb.colourant_id] : []),
  );
  // Feb 2026 — Phase 2 migration (defect A). reqId stale-guard.
  const reqIdRef = useRef(0);

  useEffect(() => {
    const t = setTimeout(async () => {
      const myReqId = ++reqIdRef.current;
      setLoading(true);
      try {
        const params = { status: "ACTIVE", limit: 200 };
        if (query.trim()) params.search = query.trim();
        const { data } = await axios.get(`${API}/colourway/colourant-skus`, { params });
        if (myReqId !== reqIdRef.current) return;
        const filtered = (data?.items || [])
          .filter((c) => requiredApps.includes(c.application))
          .filter((c) => !alreadyLinked.has(c.colourant_id));
        setItems(filtered);
      } catch (e) {
        if (myReqId !== reqIdRef.current) return;
        toast.error(backendErrorMessage(e, "Search failed"));
      } finally {
        if (myReqId === reqIdRef.current) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const submit = async () => {
    if (!picked) return toast.error("Pick a Colourant SKU to link");
    setSubmitting(true);
    try {
      await axios.post(`${API}/colourway/vendor-mbs/attach-existing`, {
        source_rm_id: mb.rm_id,
        colourant_id: picked.colourant_id,
        vendor_mb_status: status,
      });
      toast.success(`${mb.rm_id} linked to ${picked.colourant_id}`);
      onAttached();
    } catch (e) {
      toast.error(backendErrorMessage(e, "Attach failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid={`link-mb-to-colourant-dialog-${mb.rm_id}`}>
        <DialogHeader>
          <DialogTitle>Link {mb.rm_id} to a Colourant SKU</DialogTitle>
          <DialogDescription>
            Adds another colourant link to this {mb.category === "PWD" ? "powder" : "MB"}.
            Compatible applications: <span className="font-mono">{requiredApps.join(" / ")}</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-stone-400" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search colourant_id / pantone…"
              className="pl-9"
              data-testid="link-mb-colourant-search-input"
            />
          </div>

          <div className="border rounded max-h-64 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Colourant ID</TableHead>
                  <TableHead>Pantone</TableHead>
                  <TableHead>App</TableHead>
                  <TableHead>Polymer</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={5} className="text-center text-xs text-stone-400 py-4">Searching…</TableCell></TableRow>
                )}
                {!loading && items.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center text-xs text-stone-500 py-4" data-testid="link-mb-colourant-empty">
                    {query.trim() ? "No matches (or all compatible colourants already linked)." : "Start typing to search."}
                  </TableCell></TableRow>
                )}
                {!loading && items.map((c) => {
                  const isPicked = picked && picked.colourant_id === c.colourant_id;
                  return (
                    <TableRow
                      key={c.colourant_id}
                      className={`cursor-pointer ${isPicked ? "bg-emerald-50" : "hover:bg-stone-50"}`}
                      onClick={() => setPicked(c)}
                      data-testid={`link-mb-colourant-row-${c.colourant_id}`}
                    >
                      <TableCell>
                        <input type="radio" checked={!!isPicked} onChange={() => setPicked(c)} aria-label={`Pick ${c.colourant_id}`} />
                      </TableCell>
                      <TableCell className="font-mono text-xs">{c.colourant_id}</TableCell>
                      <TableCell className="text-xs">
                        <div className="flex items-center gap-1">
                          <ColourSwatch hex={c.color_hex} />
                          <span className="font-mono">{c.pantone_code}</span>
                        </div>
                      </TableCell>
                      <TableCell><Badge variant="secondary" className="text-[10px]">{c.application}</Badge></TableCell>
                      <TableCell className="font-mono text-xs">{c.polymer_base || "—"}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center gap-3">
            <Label className="text-xs">Status on link</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-[160px]" data-testid="link-mb-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MB_STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {status === "PREFERRED" && (
              <span className="text-[11px] text-amber-700">
                Will auto-demote the existing preferred MB for the chosen colourant.
              </span>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={!picked || submitting} data-testid="link-mb-submit">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
            Link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



/* =========================================================================
 * Bidso SKU Composer tab — the designer's flow
 * =======================================================================*/

function BidsoSKUComposerTab() {
  const [bidsoOptions, setBidsoOptions] = useState([]);
  const [bidsoTotal, setBidsoTotal] = useState(0);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [bidsoId, setBidsoId] = useState("");
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(false);
  const [swapTarget, setSwapTarget] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Filter dropdowns
  const [verticals, setVerticals] = useState([]);
  const [models, setModels] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [filterVertical, setFilterVertical] = useState("");
  const [filterModel, setFilterModel] = useState("");
  const [filterLicense, setFilterLicense] = useState("");

  // Staged colour changes for the Variant flow — keyed by `${part_rm_id}|${seq}`
  // so each Dual-Frame application can be staged independently.
  const [stagedChanges, setStagedChanges] = useState({});
  const [reviewOpen, setReviewOpen] = useState(false);
  const isMasterAdmin = useAuthStore((s) => s.isMasterAdmin());
  const stagedCount = Object.keys(stagedChanges).length;

  const stageChange = (partRmId, sequence, newPantoneCode) => {
    const key = `${partRmId}|${sequence}`;
    setStagedChanges((prev) => ({
      ...prev,
      [key]: {
        part_rm_id: partRmId,
        application_sequence: sequence,
        new_pantone_code: newPantoneCode,
      },
    }));
  };

  const unstageChange = (partRmId, sequence) => {
    const key = `${partRmId}|${sequence}`;
    setStagedChanges((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const resetStaged = () => setStagedChanges({});

  const openReview = () => {
    if (stagedCount === 0) return;
    setReviewOpen(true);
  };

  const onVariantCreated = (newBidsoSkuId) => {
    // Dialog closed AFTER showing the result step; user clicked "Open Variant".
    // Reset staged + jump the composer to the new variant.
    resetStaged();
    setReviewOpen(false);
    if (newBidsoSkuId) setBidsoId(newBidsoSkuId);
  };

  const onReviewClosed = () => {
    setReviewOpen(false);
  };

  // Fetch the bidso list (re-runs when any filter changes; also bound to
  // the Refresh button so newly-created variants surface immediately).
  const fetchBidsoOptions = async () => {
    setOptionsLoading(true);
    try {
      const params = { include_inactive: false, page_size: 10000 };
      if (filterVertical) params.vertical_id = filterVertical;
      if (filterModel) {
        const m = models.find((mm) => mm.id === filterModel);
        if (m) params.model_code = m.code;
      }
      if (filterLicense) params.license_code = filterLicense;
      const { data } = await axios.get(`${API}/sku-management/bidso-skus`, { params });
      const list = data?.items || data?.bidso_skus || (Array.isArray(data) ? data : []);
      setBidsoOptions(Array.isArray(list) ? list : []);
      setBidsoTotal(data?.total ?? (Array.isArray(list) ? list.length : 0));
    } catch (e) {
      toast.error("Failed to load Bidso SKU list");
    } finally {
      setOptionsLoading(false);
    }
  };

  // Load filter dropdowns once.
  useEffect(() => {
    (async () => {
      try {
        const [vRes, mRes, lRes] = await Promise.all([
          axios.get(`${API}/verticals`),
          axios.get(`${API}/models`),
          axios.get(`${API}/licenses`),
        ]);
        setVerticals(Array.isArray(vRes.data) ? vRes.data : (vRes.data?.items || []));
        setModels(Array.isArray(mRes.data) ? mRes.data : (mRes.data?.items || []));
        setLicenses(Array.isArray(lRes.data) ? lRes.data : (lRes.data?.items || []));
      } catch (e) {
        // Non-fatal — filters just won't have options.
      }
    })();
  }, []);

  // Re-fetch bidso list whenever the filter selection changes.
  useEffect(() => {
    fetchBidsoOptions();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [filterVertical, filterModel, filterLicense]);

  const clearFilters = () => {
    setFilterVertical("");
    setFilterModel("");
    setFilterLicense("");
  };

  // Cascade: when Vertical changes, drop any Model selection that no longer fits.
  const modelsForVertical = useMemo(() => {
    if (!filterVertical) return models;
    return models.filter((m) => m.vertical_id === filterVertical);
  }, [models, filterVertical]);

  useEffect(() => {
    if (filterModel && !modelsForVertical.some((m) => m.id === filterModel)) {
      setFilterModel("");
    }
  }, [filterVertical, filterModel, modelsForVertical]);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    if (filterVertical) {
      const v = verticals.find((x) => x.id === filterVertical);
      chips.push(`Vertical=${v?.name || v?.code || filterVertical}`);
    }
    if (filterModel) {
      const m = models.find((x) => x.id === filterModel);
      chips.push(`Model=${m?.name || m?.code || filterModel}`);
    }
    if (filterLicense) {
      const l = licenses.find((x) => x.code === filterLicense);
      chips.push(
        filterLicense === "__UNLICENSED__"
          ? "License=Unlicensed"
          : `License=${l?.name || l?.code || filterLicense}`
      );
    }
    return chips;
  }, [filterVertical, filterModel, filterLicense, verticals, models, licenses]);

  const selectedBidso = useMemo(
    () => bidsoOptions.find((b) => b.bidso_sku_id === bidsoId),
    [bidsoOptions, bidsoId]
  );

  const loadView = async (id) => {
    if (!id) { setView(null); return; }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/colourway/bidso-skus/${encodeURIComponent(id)}/colourway`);
      setView(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Failed to load Bidso SKU colourway");
      setView(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadView(bidsoId); /* eslint-disable-next-line */ }, [bidsoId]);

  const handleRefresh = () => {
    fetchBidsoOptions();
    if (bidsoId) loadView(bidsoId);
  };

  return (
    <div className="space-y-4" data-testid="composer-tab">
      <Card className="border-rose-100 bg-rose-50/30">
        <CardContent className="p-5 space-y-3">
          {/* Filter row */}
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[180px]">
              <Label className="text-[11px] text-stone-600 uppercase tracking-wide">Vertical</Label>
              <Select value={filterVertical || "__all__"} onValueChange={(v) => setFilterVertical(v === "__all__" ? "" : v)}>
                <SelectTrigger className="h-9" data-testid="composer-filter-vertical">
                  <SelectValue placeholder="All verticals" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All verticals</SelectItem>
                  {verticals.map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.name || v.code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[180px]">
              <Label className="text-[11px] text-stone-600 uppercase tracking-wide">Model</Label>
              <Select value={filterModel || "__all__"} onValueChange={(v) => setFilterModel(v === "__all__" ? "" : v)}>
                <SelectTrigger className="h-9" data-testid="composer-filter-model">
                  <SelectValue placeholder={filterVertical ? "All models in vertical" : "All models"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All models</SelectItem>
                  {modelsForVertical.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.name || m.code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[180px]">
              <Label className="text-[11px] text-stone-600 uppercase tracking-wide">License</Label>
              <Select value={filterLicense || "__all__"} onValueChange={(v) => setFilterLicense(v === "__all__" ? "" : v)}>
                <SelectTrigger className="h-9" data-testid="composer-filter-license">
                  <SelectValue placeholder="All licenses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All licenses</SelectItem>
                  <SelectItem value="__UNLICENSED__">— Unlicensed —</SelectItem>
                  {licenses.map((l) => (
                    <SelectItem key={l.code} value={l.code}>{l.name || l.code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {(filterVertical || filterModel || filterLicense) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="h-9 text-xs text-stone-600 hover:text-rose-700"
                data-testid="composer-filter-clear"
              >
                Clear
              </Button>
            )}
          </div>

          {/* Picker + Refresh */}
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-[320px]">
              <Label className="text-stone-700">Pick a Bidso SKU to compose</Label>
              <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={pickerOpen}
                    className="w-full justify-between font-normal"
                    data-testid="composer-bidso-picker"
                  >
                    {selectedBidso ? (
                      <span className="truncate">
                        <span className="font-mono text-xs">{selectedBidso.bidso_sku_id}</span>
                        <span className="ml-2 text-stone-600">{selectedBidso.name || selectedBidso.description}</span>
                      </span>
                    ) : (
                      <span className="text-stone-400">Search or pick a Bidso SKU…</span>
                    )}
                    <Search className="ml-2 w-4 h-4 shrink-0 opacity-60" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
                  <Command shouldFilter={true}>
                    <CommandInput placeholder="Type to search by code or name…" data-testid="composer-bidso-search" />
                    <CommandList>
                      <CommandEmpty>
                        {optionsLoading ? "Loading…" : "No Bidso SKU matches."}
                      </CommandEmpty>
                      <CommandGroup>
                        {bidsoOptions.map((b) => (
                          <CommandItem
                            key={b.bidso_sku_id}
                            value={`${b.bidso_sku_id} ${b.name || ""} ${b.description || ""}`}
                            onSelect={() => { setBidsoId(b.bidso_sku_id); setPickerOpen(false); }}
                            data-testid={`composer-bidso-opt-${b.bidso_sku_id}`}
                          >
                            <span className="font-mono text-xs w-28">{b.bidso_sku_id}</span>
                            <span className="ml-2 text-stone-600 truncate flex-1">{b.name || b.description}</span>
                            {b.status && b.status !== "ACTIVE" && (
                              <span
                                className={`ml-2 text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider shrink-0 ${
                                  b.status === "PENDING_SOURCING"
                                    ? "bg-amber-100 text-amber-800 border border-amber-300"
                                    : "bg-stone-100 text-stone-600 border border-stone-300"
                                }`}
                                data-testid={`composer-bidso-status-${b.bidso_sku_id}`}
                              >
                                {b.status === "PENDING_SOURCING" ? "Pending sourcing" : b.status}
                              </span>
                            )}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            {view && (
              <div className="text-sm text-stone-600">
                <div className="text-[11px] text-stone-500 uppercase tracking-wide">Loaded</div>
                <div className="font-medium">{view.bidso_sku_name || view.bidso_sku_id}</div>
              </div>
            )}
            <Button variant="outline" onClick={handleRefresh} data-testid="composer-refresh">
              ↻ Refresh
            </Button>
          </div>

          {/* Result counter / chips */}
          <div className="flex items-center gap-2 text-xs text-stone-500" data-testid="composer-result-counter">
            <span>
              Showing <span className="font-semibold text-stone-700 tabular-nums">{bidsoOptions.length}</span> of{" "}
              <span className="tabular-nums">{bidsoTotal}</span>
            </span>
            {activeFilterChips.map((c) => (
              <span key={c} className="px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 border border-rose-200 text-[10px] uppercase tracking-wide">
                {c}
              </span>
            ))}
          </div>
        </CardContent>
      </Card>

      {loading && <div className="text-center py-10 text-stone-500">Loading…</div>}

      {!loading && view && view.colourable_parts?.length === 0 && (
        <Card>
          <CardContent className="p-10 text-center text-stone-500" data-testid="composer-no-parts">
            This Bidso SKU has no colourable parts (no INP/INMFAB in its common BOM).
            {view.message && <div className="text-xs mt-2">{view.message}</div>}
          </CardContent>
        </Card>
      )}

      {!loading && view && view.colourable_parts?.length > 0 && stagedCount > 0 && (
        <div
          className="sticky top-2 z-20 bg-rose-600 text-white rounded-lg shadow-lg px-4 py-3 flex items-center justify-between"
          data-testid="staged-variant-bar"
        >
          <div className="flex items-center gap-3 text-sm">
            <Layers className="w-5 h-5" />
            <div>
              <div className="font-semibold">
                {stagedCount} colour change{stagedCount === 1 ? "" : "s"} staged
              </div>
              <div className="text-xs text-rose-100">
                Will create ONE new variant Bidso SKU with all staged changes applied.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="bg-white/10 text-white border-white/30 hover:bg-white/20"
              onClick={resetStaged}
              data-testid="staged-reset-btn"
            >
              Reset
            </Button>
            <Button
              size="sm"
              className="bg-white text-rose-700 hover:bg-rose-50"
              onClick={openReview}
              data-testid="staged-commit-btn"
            >
              {`Review & Create Variant (${stagedCount})`}
            </Button>
          </div>
        </div>
      )}

      {!loading && view && view.colourable_parts?.length > 0 && (
        <div
          className="grid gap-3 grid-cols-1 lg:grid-cols-2"
          data-testid="composer-parts-list"
        >
          {view.colourable_parts.map((p) => (
            <Card
              key={p.rm_id}
              data-testid={`composer-part-${p.rm_id}`}
              className={
                Object.keys(stagedChanges).some((k) => k.startsWith(`${p.rm_id}|`))
                  ? "border-rose-400 bg-rose-50/40"
                  : ""
              }
            >
              <CardContent className="p-3 flex items-start gap-3">
                {/* Swatch (or two swatches for DF) */}
                <div className="flex flex-col items-center gap-1 pt-0.5">
                  {(p.colourant_components || []).length >= 2 ? (
                    <div className="w-11 h-11 rounded-lg border-2 border-stone-300 shadow-inner overflow-hidden grid grid-cols-2">
                      <div style={{ background: p.colourant_components[0]?.colourant_color_hex || "#d6d3d1" }} />
                      <div style={{ background: p.colourant_components[1]?.colourant_color_hex || "#d6d3d1" }} />
                    </div>
                  ) : (
                    <div
                      className="w-11 h-11 rounded-lg border-2 border-stone-300 shadow-inner"
                      style={{
                        background: p.colourant_component?.colourant_color_hex
                          || "linear-gradient(135deg,#e7e5e4,#a8a29e)"
                      }}
                      data-testid={`part-swatch-${p.rm_id}`}
                    />
                  )}
                  <Badge variant="secondary" className="text-[10px]">{p.category}</Badge>
                  {p.is_dual_frame && (
                    <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]" data-testid={`df-badge-${p.rm_id}`}>
                      DF
                    </Badge>
                  )}
                </div>

                {/* Identity + slots */}
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="font-medium text-sm flex items-center gap-2">
                    <span className="font-mono text-xs text-stone-500">{p.rm_id}</span>
                    <span className="text-stone-300">·</span>
                    <span>{p.part_name || p.description || "—"}</span>
                  </div>
                  <div className="text-xs text-stone-500">
                    {p.mould_code && <>Mould <code className="bg-stone-100 px-1 rounded">{p.mould_code}</code> · </>}
                    Used <strong>{p.bom_quantity}</strong> {p.bom_unit}/Bidso
                    {p.polymer_component && <> · Polymer <span className="font-mono">{p.polymer_component.component_rm_id}</span></>}
                  </div>

                  {/* Colourant slots — 1 or N */}
                  <div className="space-y-1.5">
                    {(p.colourant_components || []).length === 0 && (
                      <div className="text-xs text-amber-700">No colourant component in rm_bom</div>
                    )}
                    {(p.colourant_components || []).map((c) => {
                      const stagedKey = `${p.rm_id}|${c.application_sequence}`;
                      const staged = stagedChanges[stagedKey];
                      return (
                      <div key={c.application_sequence} className="flex items-center gap-2 text-xs">
                        <Badge variant="outline" className="text-[10px] w-16 justify-center">
                          {c.application_sequence === 1 ? "Primary" : `Pass ${c.application_sequence}`}
                        </Badge>
                        <Palette className="w-3 h-3 text-stone-500" />
                        <strong>{c.colourant_pantone_code || c.pantone || "untagged"}</strong>
                        {staged && (
                          <>
                            <span className="text-rose-500">→</span>
                            <Badge className="bg-rose-600 text-white border-rose-700 text-[10px]" data-testid={`staged-badge-${p.rm_id}-seq${c.application_sequence}`}>
                              {staged.new_pantone_code} (staged)
                            </Badge>
                            <button
                              type="button"
                              onClick={() => unstageChange(p.rm_id, c.application_sequence)}
                              className="text-rose-600 hover:text-rose-800 text-[11px]"
                              data-testid={`staged-clear-${p.rm_id}-seq${c.application_sequence}`}
                              title="Remove staged change"
                            >
                              ✕
                            </button>
                          </>
                        )}
                        <span className="text-stone-400 font-mono">({c.rm_id})</span>
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => setSwapTarget({ ...p, _swap_sequence: c.application_sequence, _swap_old: c })}
                          data-testid={`part-change-${p.rm_id}-seq${c.application_sequence}`}
                          className="ml-auto h-7"
                          disabled={view.is_locked}
                        >
                          {staged ? "Edit" : "Change"}
                        </Button>
                      </div>
                      );
                    })}
                  </div>

                  {/* DF affordances for INMFAB */}
                  {(p.category === "INMFAB" || p.category === "INM") && (
                    <div className="flex gap-2 pt-1">
                      {!p.is_dual_frame ? (
                        <Button
                          size="sm" variant="outline"
                          onClick={() => setSwapTarget({ ...p, _df_setup: true })}
                          data-testid={`df-add-${p.rm_id}`}
                          disabled={!p.colourant_component || view.is_locked}
                          className="text-xs"
                        >
                          + Add Dual Frame (2nd colour)
                        </Button>
                      ) : (
                        <Button
                          size="sm" variant="ghost"
                          onClick={async () => {
                            if (!window.confirm("Remove the secondary colour and revert to single-colour frame?")) return;
                            try {
                              await axios.post(`${API}/colourway/parts/${encodeURIComponent(p.rm_id)}/clear-dual-frame`);
                              toast.success("Cleared Dual Frame");
                              loadView(bidsoId);
                            } catch (e) {
                              toast.error(e.response?.data?.detail || "Failed");
                            }
                          }}
                          data-testid={`df-clear-${p.rm_id}`}
                          className="text-xs text-rose-600"
                        >
                          Clear Dual Frame
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {swapTarget && (
        <SwapColourantDialog
          bidsoSkuId={view.bidso_sku_id}
          part={swapTarget}
          isMasterAdmin={isMasterAdmin}
          existingStaged={
            stagedChanges[`${swapTarget.rm_id}|${swapTarget._swap_sequence || 1}`]
          }
          onClose={() => setSwapTarget(null)}
          onStageChange={(pantoneCode) => {
            stageChange(swapTarget.rm_id, swapTarget._swap_sequence || 1, pantoneCode);
            setSwapTarget(null);
          }}
          onUnstage={() => {
            unstageChange(swapTarget.rm_id, swapTarget._swap_sequence || 1);
            setSwapTarget(null);
          }}
          onSwapped={() => { setSwapTarget(null); loadView(bidsoId); }}
        />
      )}

      {reviewOpen && view && (
        <VariantReviewDialog
          bidsoId={bidsoId}
          sourceView={view}
          stagedChanges={stagedChanges}
          onClose={onReviewClosed}
          onCommitted={onVariantCreated}
        />
      )}
    </div>
  );
}

function SwapColourantDialog({ bidsoSkuId, part, isMasterAdmin, existingStaged, onClose, onStageChange, onUnstage, onSwapped }) {
  const [pantoneOptions, setPantoneOptions] = useState([]);
  const [newPantone, setNewPantone] = useState(existingStaged?.new_pantone_code || "");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [searchPantone, setSearchPantone] = useState("");
  // Default mode = CLONE (per Case 2). Correction is restricted to Master Admin.
  const [mode, setMode] = useState("CLONE"); // CORRECTION | CLONE | DF_SETUP
  const [newBidsoSkuName, setNewBidsoSkuName] = useState("");

  // Initialise mode from caller context
  useEffect(() => {
    if (part?._df_setup) setMode("DF_SETUP");
    else setMode("CLONE");
  }, [part]);

  const slotLabel = part?._swap_sequence === 2 ? "Secondary (Pass 2)" : "Primary";
  const swapSequence = part?._swap_sequence || 1;
  const isDFSetup = mode === "DF_SETUP";

  const oldColourant = part?._swap_old || part?.colourant_component;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/pantone/shades`, {
          params: { status: "ACTIVE", limit: 500 },
        });
        setPantoneOptions(data?.shades || data?.items || []);
      } catch (e) { toast.error("Failed to load Pantone shades"); }
    })();
  }, []);

  const filteredOptions = useMemo(() => {
    if (!searchPantone) return pantoneOptions;
    const s = searchPantone.toLowerCase();
    return pantoneOptions.filter(
      (p) => p.pantone_code?.toLowerCase().includes(s) ||
             p.pantone_name?.toLowerCase().includes(s) ||
             p.color_family?.toLowerCase().includes(s)
    );
  }, [pantoneOptions, searchPantone]);

  const submit = async () => {
    if (!newPantone) return toast.error("Pick a Pantone shade");
    setSubmitting(true);
    try {
      if (mode === "DF_SETUP") {
        const { data } = await axios.post(
          `${API}/colourway/parts/${encodeURIComponent(part.rm_id)}/set-dual-frame`,
          { secondary_pantone_code: newPantone, reason: reason || null }
        );
        toast.success(`Dual Frame set: secondary ${data.secondary_pantone_code}`);
      } else if (mode === "CORRECTION") {
        const { data } = await axios.post(
          `${API}/colourway/bidso-skus/${encodeURIComponent(bidsoSkuId)}/swap-colourant`,
          { part_rm_id: part.rm_id, new_pantone_code: newPantone, reason: reason || null,
            application_sequence: swapSequence }
        );
        if (data.no_op) {
          toast.info(data.message || "Already that colour — no change.");
        } else {
          toast.success(`Swapped ${data.old_mb_rm_id || "—"} → ${data.new_mb_rm_id}`, { duration: 5000 });
          if (data.open_po_advisory?.length > 0) {
            toast.warning(
              `Advisory: ${data.open_po_advisory.length} open PO(s) reference the OLD MB — review with procurement`,
              { duration: 8000 }
            );
          }
        }
      } else {
        // CLONE — bidso_sku_id is auto-generated server-side from parent's vertical+model
        const { data } = await axios.post(
          `${API}/colourway/bidso-skus/${encodeURIComponent(bidsoSkuId)}/clone-as-variant`,
          {
            part_rm_id: part.rm_id,
            new_pantone_code: newPantone,
            new_bidso_sku_name: newBidsoSkuName.trim() || null,
            reason: reason || null,
          }
        );
        toast.success(
          `Cloned to ${data.new_bidso_sku_id} (new part ${data.new_part_rm_id})`,
          { duration: 7000 }
        );
      }
      onSwapped();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "NO_MATCHING_COLOURANT") {
        toast.error(d.message, { duration: 10000 });
      } else if (d?.code === "NO_USABLE_VENDOR_MB") {
        toast.error(d.message, { duration: 10000 });
      } else {
        toast.error(typeof d === "string" ? d : d?.message || "Swap failed");
      }
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="swap-colourant-dialog">
        <DialogHeader>
          <DialogTitle>
            {isDFSetup
              ? `Add Dual Frame (2nd colour): ${part.part_name || part.rm_id}`
              : `Change colour: ${part.part_name || part.rm_id}`}
            {!isDFSetup && part?._swap_sequence && (
              <Badge variant="secondary" className="ml-2 text-xs">{slotLabel}</Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {isDFSetup
              ? "Pick the Pantone shade for the SECOND powder-coat pass. Primary stays untouched."
              : "Pick the new Pantone shade. The system finds the matching Colourant SKU and procurement-preferred Vendor MB, then swaps it into the part's BOM."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Mode toggle — hidden for DF_SETUP and for non-Master-Admin users.
              Non-admins see only the Variant (clone) flow per Case 2. */}
          {!isDFSetup && isMasterAdmin && (
            <>
              <div className="flex items-center gap-2 bg-white border border-stone-200 rounded-lg p-1">
                <button
                  type="button"
                  onClick={() => setMode("CORRECTION")}
                  className={`flex-1 px-3 py-2 text-sm rounded-md transition ${mode === "CORRECTION" ? "bg-rose-600 text-white font-medium" : "text-stone-700 hover:bg-stone-50"}`}
                  data-testid="swap-mode-correction"
                >
                  Correction (in-place) — admin only
                </button>
                <button
                  type="button"
                  onClick={() => setMode("CLONE")}
                  className={`flex-1 px-3 py-2 text-sm rounded-md transition ${mode === "CLONE" ? "bg-rose-600 text-white font-medium" : "text-stone-700 hover:bg-stone-50"}`}
                  data-testid="swap-mode-clone"
                >
                  New Variant (clone)
                </button>
              </div>
              <div className="text-[11px] text-stone-500">
                {mode === "CORRECTION"
                  ? "Updates the existing Bidso SKU's part directly. Commits immediately when you Confirm."
                  : "Stages a colour change. After staging changes on all the parts you want, click \"Create Variant Bidso SKU\" at the top of the parts list."}
              </div>
            </>
          )}
          {!isDFSetup && !isMasterAdmin && (
            <div className="text-[11px] text-stone-500 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2">
              Stage colour changes for multiple parts, then click <strong>Create Variant Bidso SKU</strong>
              at the top to commit them all as one new variant.
            </div>
          )}

          {!isDFSetup && (
            <div className="bg-stone-50 border border-stone-200 rounded-lg p-3 flex items-center gap-3 text-sm">
              <div
                className="w-10 h-10 rounded-md border border-stone-300"
                style={{ background: oldColourant?.colourant_color_hex || "#d6d3d1" }}
              />
              <div className="flex-1">
                <div className="text-[11px] text-stone-500 uppercase">Current colour ({slotLabel})</div>
                <div className="font-medium">
                  {oldColourant?.colourant_pantone_code || oldColourant?.pantone || "—"}
                </div>
                <div className="text-xs text-stone-500 font-mono">
                  {oldColourant?.rm_id} · {oldColourant?.description || ""}
                </div>
              </div>
            </div>
          )}

          {mode === "CLONE" && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-900 flex items-start gap-2">
              <Layers className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold">Staged change — combined into ONE new Bidso SKU</div>
                <div className="text-rose-700 mt-0.5">
                  Save this change, then stage more parts as needed. When you click
                  <strong> Create Variant Bidso SKU</strong> at the top of the parts list,
                  the system mints a single new SKU
                  (next free <code className="font-mono mx-0.5 px-1 bg-white rounded">{bidsoSkuId.replace(/_\d+$/, "_NNN")}</code>)
                  with every staged colour change baked into its BOM. Missing Colourant
                  SKUs are auto-minted. Parts without a sourced Vendor MB will be flagged
                  <code className="font-mono mx-1 px-1 bg-white rounded">PENDING_SOURCING</code>.
                </div>
              </div>
            </div>
          )}

          <div>
            <Label>Pick new Pantone shade <span className="text-rose-500">*</span></Label>
            <Input
              placeholder="Search Pantone code / name / family…"
              value={searchPantone}
              onChange={(e) => setSearchPantone(e.target.value)}
              className="mb-2"
              data-testid="swap-pantone-search"
            />
            <div className="border border-stone-200 rounded-md max-h-64 overflow-y-auto divide-y" data-testid="swap-pantone-list">
              {filteredOptions.slice(0, 50).map((p) => (
                <button
                  key={p.pantone_code}
                  type="button"
                  onClick={() => setNewPantone(p.pantone_code)}
                  className={`w-full text-left p-2 flex items-center gap-3 hover:bg-stone-50 ${newPantone === p.pantone_code ? "bg-rose-50" : ""}`}
                  data-testid={`swap-pantone-opt-${p.pantone_code}`}
                >
                  <div className="w-6 h-6 rounded border border-stone-300" style={{ background: p.color_hex || "#fff" }} />
                  <div className="flex-1">
                    <div className="font-medium text-sm">{p.pantone_code}</div>
                    <div className="text-xs text-stone-500">{p.pantone_name}</div>
                  </div>
                  <Badge variant="secondary" className="text-[10px]">{p.color_family}</Badge>
                  {newPantone === p.pantone_code && <Badge variant="default" className="bg-rose-600">Selected</Badge>}
                </button>
              ))}
              {filteredOptions.length > 50 && (
                <div className="text-xs text-stone-400 p-2 text-center">+{filteredOptions.length - 50} more — refine your search</div>
              )}
              {filteredOptions.length === 0 && (
                <div className="text-sm text-stone-400 p-4 text-center">No shades match.</div>
              )}
            </div>
          </div>

          <div>
            <Label>Reason (recommended)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this colour changing? (saved to audit log)"
              rows={2}
              data-testid="swap-reason"
            />
          </div>
        </div>

        <DialogFooter>
          {existingStaged && mode === "CLONE" && (
            <Button
              variant="outline"
              onClick={onUnstage}
              className="mr-auto text-rose-700 border-rose-300 hover:bg-rose-50"
              data-testid="swap-unstage"
            >
              Remove staged change
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => {
              // CLONE mode → just stage locally. The header-level "Create
              // Variant Bidso SKU" button commits all staged changes at once.
              if (mode === "CLONE" && !isDFSetup) {
                if (!newPantone) {
                  toast.error("Pick a Pantone shade");
                  return;
                }
                onStageChange && onStageChange(newPantone);
                toast.success(`Staged: ${part?.part_name || part?.rm_id} → ${newPantone}`);
                return;
              }
              // CORRECTION (admin-only) and DF_SETUP commit immediately
              submit();
            }}
            disabled={submitting || !newPantone}
            data-testid="swap-submit"
          >
            {submitting
              ? (isDFSetup ? "Setting…" : "Swapping…")
              : (isDFSetup
                  ? "Add Dual Frame secondary"
                  : mode === "CLONE"
                    ? (existingStaged ? "Update staged change" : "Save change")
                    : `Apply swap (${slotLabel})`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* =========================================================================
 * Change Log tab — audit feed
 * =======================================================================*/

function ChangeLogTab() {
  const [rows, setRows] = useState([]);
  const [filterBidso, setFilterBidso] = useState("");
  const [loading, setLoading] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filterBidso) params.bidso_sku_id = filterBidso;
      const { data } = await axios.get(`${API}/colourway/change-log`, { params });
      setRows(data.items || []);
    } catch (e) {
      toast.error("Failed to load change log");
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-4" data-testid="change-log-tab">
      <div className="flex flex-wrap items-center gap-3">
        <Input
          value={filterBidso}
          onChange={(e) => setFilterBidso(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && reload()}
          placeholder="Filter by Bidso SKU id…"
          className="max-w-xs"
          data-testid="change-log-filter-bidso"
        />
        <Button variant="outline" onClick={reload} data-testid="change-log-refresh">Refresh</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Bidso SKU</TableHead>
                <TableHead>Part</TableHead>
                <TableHead>From</TableHead>
                <TableHead>To</TableHead>
                <TableHead>Mode</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Reason</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={8} className="py-8 text-center text-stone-500">Loading…</TableCell></TableRow>}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={8} className="py-10 text-center text-stone-500" data-testid="change-log-empty">No colour changes yet.</TableCell></TableRow>
              )}
              {!loading && rows.map((r) => (
                <TableRow key={r.id} data-testid={`change-log-row-${r.id}`}>
                  <TableCell className="text-xs whitespace-nowrap">{r.created_at?.slice(0, 19).replace("T", " ")}</TableCell>
                  <TableCell className="font-mono text-xs">{r.bidso_sku_id}</TableCell>
                  <TableCell><span className="font-mono text-xs">{r.part_rm_id}</span><br/><span className="text-xs text-stone-500">{r.part_name}</span></TableCell>
                  <TableCell><div className="text-xs">{r.old_pantone_code || "—"}</div><div className="text-[10px] text-stone-400 font-mono">{r.old_mb_rm_id || ""}</div></TableCell>
                  <TableCell><div className="text-xs font-medium">{r.new_pantone_code}</div><div className="text-[10px] text-stone-400 font-mono">{r.new_mb_rm_id}</div></TableCell>
                  <TableCell><Badge variant="secondary">{r.mode}</Badge></TableCell>
                  <TableCell className="text-xs">{r.actor_name || r.actor_email}</TableCell>
                  <TableCell className="text-xs max-w-xs truncate" title={r.reason}>{r.reason || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}


/* =========================================================================
 * Variant Review Dialog — Review → Confirm → Result (3 internal steps)
 *
 * Triggered from the staged-bar's "Review & Create Variant" button.
 * Step 1 'review'   — shows source SKU details, highlighted staged changes,
 *                     full common BOM with about-to-be-cloned rows flagged.
 *                     User confirms or cancels.
 * Step 2 'creating' — POST runs; spinner.
 * Step 3 'result'   — shows new variant code/name, per-part old→new mapping,
 *                     full resultant BOM with newly-minted INPs highlighted.
 *                     User can "Open Variant" or "Close".
 * =======================================================================*/

function VariantReviewDialog({ bidsoId, sourceView, stagedChanges, onClose, onCommitted }) {
  const [step, setStep] = useState("review"); // review | creating | result
  const [sourceBom, setSourceBom] = useState(null);
  const [resultBom, setResultBom] = useState(null);
  const [resultResp, setResultResp] = useState(null);
  const [pantoneIndex, setPantoneIndex] = useState({}); // pantone_code -> shade doc
  const [error, setError] = useState(null);

  const stagedList = useMemo(() => Object.values(stagedChanges), [stagedChanges]);
  const changedPartRmIds = useMemo(
    () => new Set(stagedList.map((s) => s.part_rm_id)),
    [stagedList]
  );

  // Load source BOM + Pantone master for hex lookups on the new shades
  useEffect(() => {
    (async () => {
      try {
        const [bomRes, panRes] = await Promise.all([
          axios.get(`${API}/sku-management/bom/common/${encodeURIComponent(bidsoId)}`),
          axios.get(`${API}/pantone/shades`, { params: { status: "ACTIVE", limit: 500 } }),
        ]);
        setSourceBom(bomRes.data || { items: [] });
        const shades = panRes.data?.shades || panRes.data?.items || [];
        const idx = {};
        for (const s of shades) idx[s.pantone_code] = s;
        setPantoneIndex(idx);
      } catch (e) {
        setError("Failed to load BOM or Pantone master for review");
      }
    })();
  }, [bidsoId]);

  const confirmCreate = async () => {
    setStep("creating");
    setError(null);
    try {
      const part_changes = stagedList.map((c) => ({
        part_rm_id: c.part_rm_id,
        new_pantone_code: c.new_pantone_code,
        application_sequence: c.application_sequence,
      }));
      const { data } = await axios.post(
        `${API}/colourway/bidso-skus/${encodeURIComponent(bidsoId)}/clone-as-variant-multi`,
        { part_changes }
      );
      setResultResp(data);
      // Fetch resultant BOM for display
      const bomRes = await axios.get(
        `${API}/sku-management/bom/common/${encodeURIComponent(data.new_bidso_sku_id)}`
      );
      setResultBom(bomRes.data || { items: [] });
      setStep("result");
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === "string" ? d : d?.message || "Variant creation failed");
      setStep("review");
    }
  };

  // ----- Build helpers -----
  const getPartInfoFromView = (rmId) => {
    return (sourceView?.colourable_parts || []).find((p) => p.rm_id === rmId) || null;
  };

  // Lookup hex/name for a Pantone code (uses pantone_index, falls back to view data)
  const lookupShade = (code) => {
    if (!code) return null;
    const fromIdx = pantoneIndex[code];
    if (fromIdx) return { pantone_code: code, pantone_name: fromIdx.pantone_name, color_hex: fromIdx.color_hex };
    return { pantone_code: code, pantone_name: null, color_hex: null };
  };

  const newBidsoSkuId = resultResp?.new_bidso_sku_id;
  const newInpByOldRmId = useMemo(() => {
    const m = {};
    for (const r of resultResp?.part_changes || []) {
      m[r.source_part_rm_id] = r;
    }
    return m;
  }, [resultResp]);
  const newRmIds = useMemo(() => {
    const s = new Set();
    for (const r of resultResp?.part_changes || []) {
      if (r.new_part_rm_id) s.add(r.new_part_rm_id);
    }
    return s;
  }, [resultResp]);

  // ----- Render -----
  return (
    <Dialog open={true} onOpenChange={(o) => !o && step !== "creating" && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="variant-review-dialog">
        <DialogHeader>
          <DialogTitle>
            {step === "review" && "Review Variant Creation"}
            {step === "creating" && "Creating Variant…"}
            {step === "result" && (
              <span className="flex items-center gap-2">
                <span className="text-emerald-600">✓</span> Variant Created
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {step === "review" && (
              <>You're about to clone <code className="font-mono bg-stone-100 px-1 rounded">{bidsoId}</code> into a new variant Bidso SKU with <strong>{stagedList.length}</strong> colour change(s). Review below and confirm.</>
            )}
            {step === "creating" && "Cloning Bidso SKU + creating new INP parts + rewriting BOM. This may take a moment."}
            {step === "result" && (
              <>The new variant <code className="font-mono bg-stone-100 px-1 rounded">{newBidsoSkuId}</code> is live. New INPs and BOM are shown below.</>
            )}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="rounded-md border border-rose-300 bg-rose-50 text-rose-800 p-3 text-sm" data-testid="variant-review-error">
            {error}
          </div>
        )}

        {step === "review" && (
          <ReviewBody
            bidsoId={bidsoId}
            sourceView={sourceView}
            sourceBom={sourceBom}
            stagedList={stagedList}
            changedPartRmIds={changedPartRmIds}
            getPartInfoFromView={getPartInfoFromView}
            lookupShade={lookupShade}
          />
        )}

        {step === "creating" && (
          <div className="flex items-center justify-center py-16" data-testid="variant-review-creating">
            <Loader2 className="w-8 h-8 animate-spin text-rose-600" />
          </div>
        )}

        {step === "result" && resultResp && (
          <ResultBody
            resultResp={resultResp}
            resultBom={resultBom}
            sourceView={sourceView}
            getPartInfoFromView={getPartInfoFromView}
            lookupShade={lookupShade}
            newInpByOldRmId={newInpByOldRmId}
            newRmIds={newRmIds}
          />
        )}

        <DialogFooter>
          {step === "review" && (
            <>
              <Button variant="outline" onClick={onClose} data-testid="variant-review-cancel-btn">
                Cancel
              </Button>
              <Button
                onClick={confirmCreate}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                disabled={!sourceBom}
                data-testid="variant-review-confirm-btn"
              >
                Confirm & Create Variant
              </Button>
            </>
          )}
          {step === "result" && (
            <>
              <Button variant="outline" onClick={onClose} data-testid="variant-review-close-btn">
                Close
              </Button>
              <Button
                onClick={() => onCommitted(newBidsoSkuId)}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="variant-review-open-variant-btn"
              >
                Open Variant
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReviewBody({ bidsoId, sourceView, sourceBom, stagedList, changedPartRmIds, getPartInfoFromView, lookupShade }) {
  return (
    <div className="space-y-5 py-2">
      {/* Source SKU summary */}
      <Card>
        <CardContent className="p-4">
          <div className="text-[11px] uppercase tracking-wide text-stone-500 mb-1">Source Bidso SKU</div>
          <div className="flex items-baseline gap-3">
            <code className="font-mono text-base font-semibold">{bidsoId}</code>
            <span className="text-stone-600 text-sm truncate">{sourceView?.bidso_sku_name || "—"}</span>
          </div>
          <div className="mt-2 text-xs text-stone-500 flex flex-wrap gap-x-4 gap-y-1">
            <span>New Bidso SKU code: <Badge variant="secondary" className="ml-1 text-[11px]">Auto-generated on confirm</Badge></span>
            <span>Variant name: <Badge variant="secondary" className="ml-1 text-[11px]">Auto-generated on confirm</Badge></span>
          </div>
        </CardContent>
      </Card>

      {/* Staged colour changes — highlighted */}
      <div>
        <div className="text-sm font-semibold text-stone-700 mb-2">Colour changes ({stagedList.length})</div>
        <div className="space-y-2">
          {stagedList.map((s) => {
            const part = getPartInfoFromView(s.part_rm_id);
            const slot = (part?.colourant_components || []).find(
              (c) => c.application_sequence === s.application_sequence
            ) || part?.colourant_component;
            const oldHex = slot?.colourant_color_hex;
            const oldCode = slot?.colourant_pantone_code || slot?.pantone;
            const newShade = lookupShade(s.new_pantone_code);
            const key = `${s.part_rm_id}|${s.application_sequence}`;
            return (
              <Card key={key} className="border-rose-300 bg-rose-50/40" data-testid={`review-change-${s.part_rm_id}-${s.application_sequence}`}>
                <CardContent className="p-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">
                      {part?.part_name || part?.description || s.part_rm_id}
                      <span className="ml-2 font-mono text-xs text-stone-500">{s.part_rm_id}</span>
                      {s.application_sequence > 1 && (
                        <Badge variant="outline" className="ml-2 text-[10px]">Pass {s.application_sequence}</Badge>
                      )}
                    </div>
                    <div className="mt-2 flex items-center gap-3 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded border-2 border-stone-300" style={{ background: oldHex || "#e7e5e4" }} />
                        <div>
                          <div className="text-[10px] uppercase text-stone-400">From</div>
                          <div className="font-mono">{oldCode || "—"}</div>
                        </div>
                      </div>
                      <span className="text-rose-500 font-bold text-lg">→</span>
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded border-2 border-rose-400 shadow" style={{ background: newShade?.color_hex || "#e7e5e4" }} />
                        <div>
                          <div className="text-[10px] uppercase text-rose-500">To</div>
                          <div className="font-mono font-semibold">{newShade?.pantone_code}</div>
                          {newShade?.pantone_name && (
                            <div className="text-[10px] text-stone-500">{newShade.pantone_name}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Full common BOM with about-to-clone rows highlighted */}
      <div>
        <div className="text-sm font-semibold text-stone-700 mb-2">
          Full Common BOM ({sourceBom?.items?.length || 0} items)
        </div>
        <div className="text-xs text-stone-500 mb-2">
          Amber rows will be cloned to new INPs with the colour swapped. All other rows are preserved as-is in the new variant.
        </div>
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-32">RM ID</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-24 text-right">Qty</TableHead>
                  <TableHead className="w-16">Unit</TableHead>
                  <TableHead className="w-40">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!sourceBom && (
                  <TableRow><TableCell colSpan={5} className="text-center py-6 text-stone-500">Loading BOM…</TableCell></TableRow>
                )}
                {sourceBom?.items?.map((it) => {
                  const willClone = changedPartRmIds.has(it.rm_id);
                  return (
                    <TableRow
                      key={it.rm_id}
                      className={willClone ? "bg-amber-50" : ""}
                      data-testid={`review-bom-row-${it.rm_id}`}
                    >
                      <TableCell className="font-mono text-xs">{it.rm_id}</TableCell>
                      <TableCell className="text-xs">{it.rm_name || it.description || "—"}</TableCell>
                      <TableCell className="text-right text-xs">{it.quantity}</TableCell>
                      <TableCell className="text-xs">{it.unit}</TableCell>
                      <TableCell className="text-xs">
                        {willClone ? (
                          <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]">
                            Will clone → new INP
                          </Badge>
                        ) : (
                          <span className="text-stone-400 text-[11px]">Preserved as-is</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ResultBody({ resultResp, resultBom, sourceView, getPartInfoFromView, lookupShade, newInpByOldRmId, newRmIds }) {
  const partChanges = resultResp?.part_changes || [];
  const pending = resultResp?.pending_sourcing_parts || [];
  const minted = partChanges.filter((p) => p.colourant_was_minted);

  return (
    <div className="space-y-5 py-2">
      {/* Big new SKU code + status */}
      <Card className="border-emerald-300 bg-emerald-50/40">
        <CardContent className="p-4">
          <div className="text-[11px] uppercase tracking-wide text-emerald-700 mb-1">New Variant Bidso SKU</div>
          <div className="flex items-baseline gap-3 flex-wrap">
            <code className="font-mono text-lg font-bold text-emerald-800" data-testid="result-new-bidso-id">
              {resultResp.new_bidso_sku_id}
            </code>
            <Badge
              className={
                resultResp.new_bidso_sku_status === "PENDING_SOURCING"
                  ? "bg-amber-200 text-amber-900 border-amber-300"
                  : "bg-emerald-200 text-emerald-900 border-emerald-300"
              }
              data-testid="result-new-bidso-status"
            >
              {resultResp.new_bidso_sku_status}
            </Badge>
          </div>
          <div className="mt-2 text-xs text-stone-600 flex flex-wrap gap-x-4">
            <span>{partChanges.length} part(s) cloned</span>
            {minted.length > 0 && (
              <span>· {minted.length} Colourant SKU(s) auto-minted</span>
            )}
            {pending.length > 0 && (
              <span className="text-amber-700">· {pending.length} part(s) awaiting Vendor MB sourcing</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Per-part old → new mapping */}
      <div>
        <div className="text-sm font-semibold text-stone-700 mb-2">New INPs created</div>
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Source Part</TableHead>
                  <TableHead className="w-10 text-center"></TableHead>
                  <TableHead>New INP</TableHead>
                  <TableHead>New Pantone</TableHead>
                  <TableHead>MB Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {partChanges.map((r) => {
                  const sourcePart = getPartInfoFromView(r.source_part_rm_id);
                  const newShade = lookupShade(r.new_pantone_code);
                  return (
                    <TableRow key={r.new_part_rm_id} data-testid={`result-change-${r.new_part_rm_id}`}>
                      <TableCell className="text-xs">
                        <div className="font-mono">{r.source_part_rm_id}</div>
                        <div className="text-stone-500">{sourcePart?.part_name || "—"}</div>
                      </TableCell>
                      <TableCell className="text-center text-rose-500 font-bold">→</TableCell>
                      <TableCell className="text-xs">
                        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px] mr-1">NEW</Badge>
                        <code className="font-mono">{r.new_part_rm_id}</code>
                        {r.colourant_was_minted && (
                          <div className="mt-1">
                            <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">
                              Auto-minted Colourant SKU
                            </Badge>
                            <code className="ml-1 text-[10px] text-stone-500">{r.target_colourant_id}</code>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 rounded border-2 border-stone-300" style={{ background: newShade?.color_hex || "#e7e5e4" }} />
                          <span className="font-mono">{r.new_pantone_code}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.mb_status === "RESOLVED" ? (
                          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px]">
                            MB: {r.new_mb_rm_id}
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]">
                            PENDING_SOURCING
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Resultant BOM */}
      <div>
        <div className="text-sm font-semibold text-stone-700 mb-2">
          Resultant Common BOM ({resultBom?.items?.length || 0} items)
        </div>
        <div className="text-xs text-stone-500 mb-2">
          Green rows are the newly-created INPs. Everything else is preserved from the source.
        </div>
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-32">RM ID</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-24 text-right">Qty</TableHead>
                  <TableHead className="w-16">Unit</TableHead>
                  <TableHead className="w-40">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!resultBom && (
                  <TableRow><TableCell colSpan={5} className="text-center py-6 text-stone-500">Loading new BOM…</TableCell></TableRow>
                )}
                {resultBom?.items?.map((it) => {
                  const isNew = newRmIds.has(it.rm_id);
                  // Find which source rm_id it came from
                  let sourceFrom = null;
                  if (isNew) {
                    for (const [srcRmId, r] of Object.entries(newInpByOldRmId)) {
                      if (r.new_part_rm_id === it.rm_id) { sourceFrom = srcRmId; break; }
                    }
                  }
                  return (
                    <TableRow
                      key={it.rm_id}
                      className={isNew ? "bg-emerald-50" : ""}
                      data-testid={`result-bom-row-${it.rm_id}`}
                    >
                      <TableCell className="font-mono text-xs">{it.rm_id}</TableCell>
                      <TableCell className="text-xs">{it.rm_name || it.description || "—"}</TableCell>
                      <TableCell className="text-right text-xs">{it.quantity}</TableCell>
                      <TableCell className="text-xs">{it.unit}</TableCell>
                      <TableCell className="text-xs">
                        {isNew ? (
                          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px]">
                            NEW · from {sourceFrom}
                          </Badge>
                        ) : (
                          <span className="text-stone-400 text-[11px]">Preserved</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
